import { themeColors } from "@/themeColors";
/**
 * @file   src\components\HeaderButtons\Notifications.js
 * @brief  This file is responsible for notifications in page header
 * @date   June, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { pageURLs } from "@/constants";
import noImage from "@/images/no-image-small.png";
import ProfInitialsIcon from "@/images/prof-initials-icon.svg";
import NotifHandshakeIcon from "@/images/notif-handshake-icon.svg";
import MarketingIcon from "@/images/marketing.svg";
import NotifClockIcon from "@/images/notif-clock-icon.svg";
import UserIcon from "@/images/intro-prof-placeholder.svg";
import UserDefaultIcon from "@/images/v2/Profiles.svg";
import ConfettiIcon from "@/images/confetti-small-icon.svg";
import HomeIcon from "@/images/homeWhite-icn.svg";

import BedIcon from "@/images/bed-icon.svg";
import BathIcon from "@/images/bath-icon.svg";
import SqftIcon from "@/images/sqft-icon.svg";

import IntroductionSkeleton from "./IntroductionSkeleton";
import _ from "lodash";
import {
  filterArray,
  formateDate,
  getpropertyName,
  removeDecimalZeros,
  setLocalStorage,
  thousandSeperatedValue,
  timeAgo,
} from "@/helpers/common";
import {
  INVENTORY_KEY,
  IS_LAND,
  LOT_TYPES,
  WANT_AD_KEY,
} from "@/constants/common";
import moment from "moment";
import { getWantAdById } from "@/services/wantAdService";
import { getInventoryById } from "@/services/inventoryService";
import { useMLSAgentAtGitchaRedirect } from "@/hooks/useMLSAgentAtGitchaRedirect";

const Notifications = (props) => {
  const navigate = useNavigate();
  const { redirectToBLSEnv } = useMLSAgentAtGitchaRedirect();
  const {
    list,
    isLoadNotificationList,
    onDeleteInappNotification,
    updateInAppDeleteStatusOnClick,
    renewWantAd,
  } = props;
  const isListItems = list?.length > 0;

  const viewIntroductions = (wantAdId, slug, introId) => {
    introId
      ? setLocalStorage(WANT_AD_KEY, { wantAdId: wantAdId, introId: introId })
      : setLocalStorage(WANT_AD_KEY, {
          wantAdId: wantAdId,
          isFromHomeFeed: true,
        });

    const path = `${pageURLs.wantAdDetails}/${slug}`;

    redirectToBLSEnv({
      path,
      onGitchaEnvFallback: () => navigate(path),
    });
  };

  const redirectHomePageBasedOnLocationSearch = (location) => {
    setLocalStorage("LocationString", location);
    setLocalStorage("FilterTabKey", "General");
    navigate(pageURLs.home);
  };

  const redirectToAllIntroduction = (inventoryId, location) => {
    setLocalStorage(INVENTORY_KEY, {
      inventoryId: inventoryId,
      isFromListingPage: true,
    });
    navigate("/view-all-introductions", {
      state: {
        inventoryId: inventoryId,
        title: location,
      },
    });
  };

  /**
   * get want-ad details for edit
   * @param {*} wantAdId
   */
  const getWantAdDetails = async (wantAdId) => {
    if (wantAdId) {
      await getWantAdById(wantAdId)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            redirectToBLSEnv({
              path: `${pageURLs.agentCreatewantad}?wantAdId=${wantAdId}`,
              onGitchaEnvFallback: () => {
                navigate(`${pageURLs.editWantAdForAgents}/${wantAdId}`, {
                  state: { data: responseResult },
                });
              },
            });
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };
  /** Notification for time remaining for an introduction to respond */
  function RenderIntroductionNotification({ notification }) {
    const bedRooms = _.get(notification, "Bedrooms", null);
    const bathRooms = _.get(notification, "Bathrooms", null);
    const sqFt = _.get(notification, "Sqft", null);
    const firstName = _.get(notification, "FirstName", "");
    const lastName = _.get(notification, "LastName", "");
    const fullName = firstName + " " + lastName;
    const askingPrice = _.get(notification, "AskingPrice", "");
    const lotType = _.get(notification, "LotType_Rural_City", "");
    const readStatus = _.get(notification, "Read_Status", 1); // 1 for unread message
    const wantAdId = _.get(notification, "Want_ad_Id", "");
    const slug = _.get(notification, "slug", "");
    const IntroductionId = _.get(notification, "IntroductionId", "");
    const inventoryImage =
      _.get(notification, "Photo_1", "") !== "null" &&
      _.get(notification, "Photo_1", "") !== "undefined" &&
      _.get(notification, "Photo_1", "") !== null
        ? _.get(notification, "Photo_1", "")
        : noImage;
    const nameOfTheIntroducer = fullName?.replace(/-/g, "")?.match(/\b(\w)/g);
    const NotificationId = _.get(notification, "NotificationId", "");
    const notificationCreatedDate = _.get(notification, "LastUpdatedTS", null);
    const acreage = _.get(notification, "TotalAcre", "");

    return (
      <div className="notification-sec notify-info-bg position-relative ">
        <button
          className="btn notif-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            updateInAppDeleteStatusOnClick(
              NotificationId,
              notification?.SubjectType,
              true,
              1,
            );
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.9999 2C10.2458 2 8.78514 3.31072 8.53998 5H5.11908C5.0765 4.99271 5.03337 4.98912 4.99017 4.98926C4.95285 4.99006 4.91564 4.99365 4.87885 5H3.24994C3.15056 4.99859 3.05188 5.01696 2.95966 5.05402C2.86743 5.09108 2.78349 5.1461 2.71271 5.21588C2.64194 5.28566 2.58573 5.36882 2.54737 5.46051C2.50901 5.5522 2.48926 5.65061 2.48926 5.75C2.48926 5.84939 2.50901 5.9478 2.54737 6.03949C2.58573 6.13118 2.64194 6.21434 2.71271 6.28412C2.78349 6.3539 2.86743 6.40892 2.95966 6.44598C3.05188 6.48304 3.15056 6.50141 3.24994 6.5H4.31928L5.57806 19.5146C5.7136 20.918 6.90583 22 8.31537 22H15.6835C17.0931 22 18.2854 20.9181 18.4208 19.5146L19.6806 6.5H20.7499C20.8493 6.50141 20.948 6.48304 21.0402 6.44598C21.1324 6.40892 21.2164 6.3539 21.2872 6.28412C21.3579 6.21434 21.4141 6.13118 21.4525 6.03949C21.4909 5.9478 21.5106 5.84939 21.5106 5.75C21.5106 5.65061 21.4909 5.5522 21.4525 5.46051C21.4141 5.36882 21.3579 5.28566 21.2872 5.21588C21.2164 5.1461 21.1324 5.09108 21.0402 5.05402C20.948 5.01696 20.8493 4.99859 20.7499 5H19.122C19.0425 4.98709 18.9613 4.98709 18.8818 5H15.4599C15.2147 3.31072 13.7541 2 11.9999 2ZM11.9999 3.5C12.9395 3.5 13.7103 4.13408 13.9306 5H10.0693C10.2896 4.13408 11.0604 3.5 11.9999 3.5ZM5.82513 6.5H18.1738L16.9277 19.3701C16.8652 20.0177 16.3339 20.5 15.6835 20.5H8.31537C7.66591 20.5 7.13369 20.0168 7.07123 19.3701L5.82513 6.5ZM10.2382 8.98926C10.0395 8.99236 9.8501 9.07423 9.71167 9.21686C9.57324 9.3595 9.49709 9.55125 9.49994 9.75V17.25C9.49853 17.3494 9.5169 17.4481 9.55396 17.5403C9.59102 17.6325 9.64604 17.7164 9.71582 17.7872C9.7856 17.858 9.86876 17.9142 9.96045 17.9526C10.0521 17.9909 10.1505 18.0107 10.2499 18.0107C10.3493 18.0107 10.4477 17.9909 10.5394 17.9526C10.6311 17.9142 10.7143 17.858 10.7841 17.7872C10.8538 17.7164 10.9089 17.6325 10.9459 17.5403C10.983 17.4481 11.0013 17.3494 10.9999 17.25V9.75C11.0014 9.64962 10.9827 9.54997 10.9449 9.45695C10.9071 9.36394 10.851 9.27946 10.78 9.20852C10.709 9.13758 10.6244 9.08161 10.5313 9.04395C10.4383 9.00629 10.3386 8.98769 10.2382 8.98926ZM13.7382 8.98926C13.5395 8.99236 13.3501 9.07423 13.2117 9.21686C13.0732 9.3595 12.9971 9.55125 12.9999 9.75V17.25C12.9985 17.3494 13.0169 17.4481 13.054 17.5403C13.091 17.6325 13.146 17.7164 13.2158 17.7872C13.2856 17.858 13.3688 17.9142 13.4605 17.9526C13.5521 17.9909 13.6505 18.0107 13.7499 18.0107C13.8493 18.0107 13.9477 17.9909 14.0394 17.9526C14.1311 17.9142 14.2143 17.858 14.2841 17.7872C14.3538 17.7164 14.4089 17.6325 14.4459 17.5403C14.483 17.4481 14.5013 17.3494 14.4999 17.25V9.75C14.5014 9.64962 14.4827 9.54997 14.4449 9.45695C14.4071 9.36394 14.351 9.27946 14.28 9.20852C14.209 9.13758 14.1244 9.08161 14.0313 9.04395C13.9383 9.00629 13.8386 8.98769 13.7382 8.98926Z"
              fill={themeColors.neutral890}
            />
          </svg>
        </button>

        <div className="outer-property-wrap pl-20 pr-20 pt-12 pb-12 ">
          <div className="d-flex mb-0 flex-grow-1 align-items-center">
            <div className="intro-prof-wrap profile-bg-meroon flex-shrink-0">
              <img className="prof-init-icon" src={NotifHandshakeIcon} />
            </div>
            <div className="introduction-content flex-grow-1 ml-10 pe-0 ps-0 pb-0">
              <p className="mb-0 notification-head">
                You have{" "}
                <span className="f-bold">
                  {_.get(notification, "SubjectType", "") == 1 ? "48 " : "24 "}
                  hours left
                </span>{" "}
                to message {firstName} before this introduction expires.{" "}
                <span className="time-stamp">
                  {" "}
                  {notificationCreatedDate &&
                    timeAgo(moment.utc(notificationCreatedDate).toISOString())}
                </span>
              </p>
            </div>
          </div>
          {readStatus == 1 && (
            <div className="dot-indicator dot-bg-red align-self-center mt-0"></div>
          )}
        </div>
        <div className="add-your-property-wrap mb-0 ml-20 mr-20 mb-12 bg-white notif-brdr">
          <div className="make-intro-img flex-shrink-0">
            <img src={inventoryImage} />
          </div>
          <div className="make-intro-details pe-1">
            <h3 className="mb-0 long-txt-fix intro-head-medium">
              {getpropertyName(notification)} in{" "}
              {_.get(notification, "InventoryCity", "")},{" "}
              {_.get(notification, "InventoryState", "")}
            </h3>

            {_.get(notification, "PropertyTypeId", "") === IS_LAND ? (
              <ul className="intro-icons-list">
                {acreage && (
                  <li>
                    <span className="list-txt">
                      {removeDecimalZeros(acreage)}
                      {_.get(notification, "Acre_Sqft", "")}
                    </span>
                    <div className="	list-icns">
                      <img src={SqftIcon} />
                    </div>
                  </li>
                )}

                {lotType && (
                  <li>
                    <span className="list-txt">
                      {filterArray(LOT_TYPES, "value", lotType)}
                    </span>
                  </li>
                )}
              </ul>
            ) : (
              <ul className="intro-icons-list">
                {bedRooms && (
                  <li>
                    <span className="list-txt">
                      {removeDecimalZeros(bedRooms)}{" "}
                    </span>
                    <div className="	list-icns">
                      <img src={BedIcon} />
                    </div>
                  </li>
                )}

                {bathRooms && (
                  <li>
                    <span className="list-txt">
                      {removeDecimalZeros(bathRooms)}{" "}
                    </span>
                    <div className="	list-icns">
                      <img src={BathIcon} />
                    </div>
                  </li>
                )}
                {sqFt && (
                  <li>
                    <span className="list-txt">
                      {removeDecimalZeros(sqFt)}
                      {" SQFT "}
                    </span>
                    <div className="	list-icns">
                      <img src={SqftIcon} />
                    </div>
                  </li>
                )}
              </ul>
            )}

            <div className="intro-amt">
              ${askingPrice && thousandSeperatedValue(askingPrice)}
            </div>
          </div>

          <button
            type="button"
            className="btn btn-outline-primary "
            onClick={(e) => {
              viewIntroductions(wantAdId, slug, IntroductionId);
              e.stopPropagation();
            }}
          >
            View
          </button>
        </div>
      </div>
    );
  }
  /** Notification for  sent introduction expired */

  function RenderIntroductionSentExpiredNotification({ notification }) {
    return (
      <div
        className="notification-sec position-relative notify-info-bg pointer"
        role="button"
        onKeyDown={(e) => {
          redirectToAllIntroduction(
            notification?.Inv_Id,
            notification?.Location,
          );

          e.preventDefault();
        }}
        onClick={(e) => {
          redirectToAllIntroduction(
            notification?.Inv_Id,
            notification?.Location,
          );

          e.preventDefault();
        }}
      >
        <button
          className="btn notif-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            updateInAppDeleteStatusOnClick(
              _.get(notification, "NotificationId", ""),
              notification?.SubjectType,
              true,
              1,
            );
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.9999 2C10.2458 2 8.78514 3.31072 8.53998 5H5.11908C5.0765 4.99271 5.03337 4.98912 4.99017 4.98926C4.95285 4.99006 4.91564 4.99365 4.87885 5H3.24994C3.15056 4.99859 3.05188 5.01696 2.95966 5.05402C2.86743 5.09108 2.78349 5.1461 2.71271 5.21588C2.64194 5.28566 2.58573 5.36882 2.54737 5.46051C2.50901 5.5522 2.48926 5.65061 2.48926 5.75C2.48926 5.84939 2.50901 5.9478 2.54737 6.03949C2.58573 6.13118 2.64194 6.21434 2.71271 6.28412C2.78349 6.3539 2.86743 6.40892 2.95966 6.44598C3.05188 6.48304 3.15056 6.50141 3.24994 6.5H4.31928L5.57806 19.5146C5.7136 20.918 6.90583 22 8.31537 22H15.6835C17.0931 22 18.2854 20.9181 18.4208 19.5146L19.6806 6.5H20.7499C20.8493 6.50141 20.948 6.48304 21.0402 6.44598C21.1324 6.40892 21.2164 6.3539 21.2872 6.28412C21.3579 6.21434 21.4141 6.13118 21.4525 6.03949C21.4909 5.9478 21.5106 5.84939 21.5106 5.75C21.5106 5.65061 21.4909 5.5522 21.4525 5.46051C21.4141 5.36882 21.3579 5.28566 21.2872 5.21588C21.2164 5.1461 21.1324 5.09108 21.0402 5.05402C20.948 5.01696 20.8493 4.99859 20.7499 5H19.122C19.0425 4.98709 18.9613 4.98709 18.8818 5H15.4599C15.2147 3.31072 13.7541 2 11.9999 2ZM11.9999 3.5C12.9395 3.5 13.7103 4.13408 13.9306 5H10.0693C10.2896 4.13408 11.0604 3.5 11.9999 3.5ZM5.82513 6.5H18.1738L16.9277 19.3701C16.8652 20.0177 16.3339 20.5 15.6835 20.5H8.31537C7.66591 20.5 7.13369 20.0168 7.07123 19.3701L5.82513 6.5ZM10.2382 8.98926C10.0395 8.99236 9.8501 9.07423 9.71167 9.21686C9.57324 9.3595 9.49709 9.55125 9.49994 9.75V17.25C9.49853 17.3494 9.5169 17.4481 9.55396 17.5403C9.59102 17.6325 9.64604 17.7164 9.71582 17.7872C9.7856 17.858 9.86876 17.9142 9.96045 17.9526C10.0521 17.9909 10.1505 18.0107 10.2499 18.0107C10.3493 18.0107 10.4477 17.9909 10.5394 17.9526C10.6311 17.9142 10.7143 17.858 10.7841 17.7872C10.8538 17.7164 10.9089 17.6325 10.9459 17.5403C10.983 17.4481 11.0013 17.3494 10.9999 17.25V9.75C11.0014 9.64962 10.9827 9.54997 10.9449 9.45695C10.9071 9.36394 10.851 9.27946 10.78 9.20852C10.709 9.13758 10.6244 9.08161 10.5313 9.04395C10.4383 9.00629 10.3386 8.98769 10.2382 8.98926ZM13.7382 8.98926C13.5395 8.99236 13.3501 9.07423 13.2117 9.21686C13.0732 9.3595 12.9971 9.55125 12.9999 9.75V17.25C12.9985 17.3494 13.0169 17.4481 13.054 17.5403C13.091 17.6325 13.146 17.7164 13.2158 17.7872C13.2856 17.858 13.3688 17.9142 13.4605 17.9526C13.5521 17.9909 13.6505 18.0107 13.7499 18.0107C13.8493 18.0107 13.9477 17.9909 14.0394 17.9526C14.1311 17.9142 14.2143 17.858 14.2841 17.7872C14.3538 17.7164 14.4089 17.6325 14.4459 17.5403C14.483 17.4481 14.5013 17.3494 14.4999 17.25V9.75C14.5014 9.64962 14.4827 9.54997 14.4449 9.45695C14.4071 9.36394 14.351 9.27946 14.28 9.20852C14.209 9.13758 14.1244 9.08161 14.0313 9.04395C13.9383 9.00629 13.8386 8.98769 13.7382 8.98926Z"
              fill={themeColors.neutral890}
            />
          </svg>
        </button>
        <div className="outer-property-wrap pl-20 pr-20 pt-12 pb-12">
          <div className="d-flex mb-0 flex-grow-1 align-items-center">
            <div className="intro-prof-wrap profile-bg-darkmeroon flex-shrink-0">
              <img className="prof-init-icon" src={NotifClockIcon} />
            </div>
            <div className="introduction-content flex-grow-1 ml-10 pe-0 ps-0 pb-0">
              <p className="mb-0 notification-head">
                Your introduction sent to want-listing{" "}
                <span className="f-bold">{notification?.wantAdTitle}</span> has
                expired{" "}
                <span className="time-stamp">
                  {notification?.LastUpdatedTS &&
                    timeAgo(
                      moment.utc(notification?.LastUpdatedTS).toISOString(),
                    )}
                </span>
              </p>
            </div>
          </div>
          {notification?.Read_Status == 1 && (
            <div className="dot-indicator dot-bg-red align-self-center mt-0"></div>
          )}
        </div>
      </div>
    );
  }

  /** Notification for  received introduction expired */

  function RenderIntroductionReceiveExpiredNotification({ notification }) {
    return (
      <div
        className="notification-sec position-relative notify-info-bg pointer"
        onClick={(e) => {
          viewIntroductions(
            _.get(notification, "Want_ad_Id", ""),
            _.get(notification, "slug", ""),
            null,
          );
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          viewIntroductions(
            _.get(notification, "Want_ad_Id", ""),
            _.get(notification, "slug", ""),
            null,
          );
          e.stopPropagation();
        }}
        role="button"
      >
        <button
          className="btn notif-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            updateInAppDeleteStatusOnClick(
              _.get(notification, "NotificationId", ""),
              notification?.SubjectType,
              true,
              1,
            );
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.9999 2C10.2458 2 8.78514 3.31072 8.53998 5H5.11908C5.0765 4.99271 5.03337 4.98912 4.99017 4.98926C4.95285 4.99006 4.91564 4.99365 4.87885 5H3.24994C3.15056 4.99859 3.05188 5.01696 2.95966 5.05402C2.86743 5.09108 2.78349 5.1461 2.71271 5.21588C2.64194 5.28566 2.58573 5.36882 2.54737 5.46051C2.50901 5.5522 2.48926 5.65061 2.48926 5.75C2.48926 5.84939 2.50901 5.9478 2.54737 6.03949C2.58573 6.13118 2.64194 6.21434 2.71271 6.28412C2.78349 6.3539 2.86743 6.40892 2.95966 6.44598C3.05188 6.48304 3.15056 6.50141 3.24994 6.5H4.31928L5.57806 19.5146C5.7136 20.918 6.90583 22 8.31537 22H15.6835C17.0931 22 18.2854 20.9181 18.4208 19.5146L19.6806 6.5H20.7499C20.8493 6.50141 20.948 6.48304 21.0402 6.44598C21.1324 6.40892 21.2164 6.3539 21.2872 6.28412C21.3579 6.21434 21.4141 6.13118 21.4525 6.03949C21.4909 5.9478 21.5106 5.84939 21.5106 5.75C21.5106 5.65061 21.4909 5.5522 21.4525 5.46051C21.4141 5.36882 21.3579 5.28566 21.2872 5.21588C21.2164 5.1461 21.1324 5.09108 21.0402 5.05402C20.948 5.01696 20.8493 4.99859 20.7499 5H19.122C19.0425 4.98709 18.9613 4.98709 18.8818 5H15.4599C15.2147 3.31072 13.7541 2 11.9999 2ZM11.9999 3.5C12.9395 3.5 13.7103 4.13408 13.9306 5H10.0693C10.2896 4.13408 11.0604 3.5 11.9999 3.5ZM5.82513 6.5H18.1738L16.9277 19.3701C16.8652 20.0177 16.3339 20.5 15.6835 20.5H8.31537C7.66591 20.5 7.13369 20.0168 7.07123 19.3701L5.82513 6.5ZM10.2382 8.98926C10.0395 8.99236 9.8501 9.07423 9.71167 9.21686C9.57324 9.3595 9.49709 9.55125 9.49994 9.75V17.25C9.49853 17.3494 9.5169 17.4481 9.55396 17.5403C9.59102 17.6325 9.64604 17.7164 9.71582 17.7872C9.7856 17.858 9.86876 17.9142 9.96045 17.9526C10.0521 17.9909 10.1505 18.0107 10.2499 18.0107C10.3493 18.0107 10.4477 17.9909 10.5394 17.9526C10.6311 17.9142 10.7143 17.858 10.7841 17.7872C10.8538 17.7164 10.9089 17.6325 10.9459 17.5403C10.983 17.4481 11.0013 17.3494 10.9999 17.25V9.75C11.0014 9.64962 10.9827 9.54997 10.9449 9.45695C10.9071 9.36394 10.851 9.27946 10.78 9.20852C10.709 9.13758 10.6244 9.08161 10.5313 9.04395C10.4383 9.00629 10.3386 8.98769 10.2382 8.98926ZM13.7382 8.98926C13.5395 8.99236 13.3501 9.07423 13.2117 9.21686C13.0732 9.3595 12.9971 9.55125 12.9999 9.75V17.25C12.9985 17.3494 13.0169 17.4481 13.054 17.5403C13.091 17.6325 13.146 17.7164 13.2158 17.7872C13.2856 17.858 13.3688 17.9142 13.4605 17.9526C13.5521 17.9909 13.6505 18.0107 13.7499 18.0107C13.8493 18.0107 13.9477 17.9909 14.0394 17.9526C14.1311 17.9142 14.2143 17.858 14.2841 17.7872C14.3538 17.7164 14.4089 17.6325 14.4459 17.5403C14.483 17.4481 14.5013 17.3494 14.4999 17.25V9.75C14.5014 9.64962 14.4827 9.54997 14.4449 9.45695C14.4071 9.36394 14.351 9.27946 14.28 9.20852C14.209 9.13758 14.1244 9.08161 14.0313 9.04395C13.9383 9.00629 13.8386 8.98769 13.7382 8.98926Z"
              fill={themeColors.neutral890}
            />
          </svg>
        </button>
        <div className="outer-property-wrap pl-20 pr-20 pt-12 pb-12">
          <div className="d-flex mb-0 flex-grow-1 align-items-center">
            <div className="intro-prof-wrap profile-bg-darkmeroon flex-shrink-0">
              <img className="prof-init-icon" src={NotifClockIcon} />
            </div>
            <div className="introduction-content flex-grow-1 ml-10 pe-0 ps-0 pb-0">
              <p className="mb-0 notification-head">
                The introduction you accepted for{" "}
                <span className="f-bold">{notification?.wantAdTitle}</span> has
                expired{" "}
                <span className="time-stamp">
                  {notification?.LastUpdatedTS &&
                    timeAgo(
                      moment.utc(notification?.LastUpdatedTS).toISOString(),
                    )}
                </span>
              </p>
            </div>
          </div>
          {notification?.Read_Status == 1 && (
            <div className="dot-indicator dot-bg-red align-self-center mt-0"></div>
          )}
        </div>
      </div>
    );
  }
  /** Notification for  want ad view */

  function RenderWantAdViewNotification({ notification }) {
    const readStatus = _.get(notification, "Read_Status", 1); // 1 for unread message
    return (
      <div
        className="notification-sec position-relative pointer"
        role="button"
        onClick={(e) => {
          e.stopPropagation();
          viewIntroductions(
            _.get(notification, "Want_ad_Id", ""),
            _.get(notification, "slug", ""),
            null,
          );
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          viewIntroductions(
            _.get(notification, "Want_ad_Id", ""),
            _.get(notification, "slug", ""),
            null,
          );
        }}
      >
        <button
          className="btn notif-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            updateInAppDeleteStatusOnClick(
              _.get(notification, "NotificationId", ""),
              notification?.SubjectType,
              true,
              1,
            );
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.9999 2C10.2458 2 8.78514 3.31072 8.53998 5H5.11908C5.0765 4.99271 5.03337 4.98912 4.99017 4.98926C4.95285 4.99006 4.91564 4.99365 4.87885 5H3.24994C3.15056 4.99859 3.05188 5.01696 2.95966 5.05402C2.86743 5.09108 2.78349 5.1461 2.71271 5.21588C2.64194 5.28566 2.58573 5.36882 2.54737 5.46051C2.50901 5.5522 2.48926 5.65061 2.48926 5.75C2.48926 5.84939 2.50901 5.9478 2.54737 6.03949C2.58573 6.13118 2.64194 6.21434 2.71271 6.28412C2.78349 6.3539 2.86743 6.40892 2.95966 6.44598C3.05188 6.48304 3.15056 6.50141 3.24994 6.5H4.31928L5.57806 19.5146C5.7136 20.918 6.90583 22 8.31537 22H15.6835C17.0931 22 18.2854 20.9181 18.4208 19.5146L19.6806 6.5H20.7499C20.8493 6.50141 20.948 6.48304 21.0402 6.44598C21.1324 6.40892 21.2164 6.3539 21.2872 6.28412C21.3579 6.21434 21.4141 6.13118 21.4525 6.03949C21.4909 5.9478 21.5106 5.84939 21.5106 5.75C21.5106 5.65061 21.4909 5.5522 21.4525 5.46051C21.4141 5.36882 21.3579 5.28566 21.2872 5.21588C21.2164 5.1461 21.1324 5.09108 21.0402 5.05402C20.948 5.01696 20.8493 4.99859 20.7499 5H19.122C19.0425 4.98709 18.9613 4.98709 18.8818 5H15.4599C15.2147 3.31072 13.7541 2 11.9999 2ZM11.9999 3.5C12.9395 3.5 13.7103 4.13408 13.9306 5H10.0693C10.2896 4.13408 11.0604 3.5 11.9999 3.5ZM5.82513 6.5H18.1738L16.9277 19.3701C16.8652 20.0177 16.3339 20.5 15.6835 20.5H8.31537C7.66591 20.5 7.13369 20.0168 7.07123 19.3701L5.82513 6.5ZM10.2382 8.98926C10.0395 8.99236 9.8501 9.07423 9.71167 9.21686C9.57324 9.3595 9.49709 9.55125 9.49994 9.75V17.25C9.49853 17.3494 9.5169 17.4481 9.55396 17.5403C9.59102 17.6325 9.64604 17.7164 9.71582 17.7872C9.7856 17.858 9.86876 17.9142 9.96045 17.9526C10.0521 17.9909 10.1505 18.0107 10.2499 18.0107C10.3493 18.0107 10.4477 17.9909 10.5394 17.9526C10.6311 17.9142 10.7143 17.858 10.7841 17.7872C10.8538 17.7164 10.9089 17.6325 10.9459 17.5403C10.983 17.4481 11.0013 17.3494 10.9999 17.25V9.75C11.0014 9.64962 10.9827 9.54997 10.9449 9.45695C10.9071 9.36394 10.851 9.27946 10.78 9.20852C10.709 9.13758 10.6244 9.08161 10.5313 9.04395C10.4383 9.00629 10.3386 8.98769 10.2382 8.98926ZM13.7382 8.98926C13.5395 8.99236 13.3501 9.07423 13.2117 9.21686C13.0732 9.3595 12.9971 9.55125 12.9999 9.75V17.25C12.9985 17.3494 13.0169 17.4481 13.054 17.5403C13.091 17.6325 13.146 17.7164 13.2158 17.7872C13.2856 17.858 13.3688 17.9142 13.4605 17.9526C13.5521 17.9909 13.6505 18.0107 13.7499 18.0107C13.8493 18.0107 13.9477 17.9909 14.0394 17.9526C14.1311 17.9142 14.2143 17.858 14.2841 17.7872C14.3538 17.7164 14.4089 17.6325 14.4459 17.5403C14.483 17.4481 14.5013 17.3494 14.4999 17.25V9.75C14.5014 9.64962 14.4827 9.54997 14.4449 9.45695C14.4071 9.36394 14.351 9.27946 14.28 9.20852C14.209 9.13758 14.1244 9.08161 14.0313 9.04395C13.9383 9.00629 13.8386 8.98769 13.7382 8.98926Z"
              fill={themeColors.neutral890}
            />
          </svg>
        </button>
        <div className="outer-property-wrap pl-20 pr-20 pt-12 pb-12">
          <div className="d-flex mb-0 flex-grow-1 align-items-center">
            <div className="intro-prof-wrap profile-bg-paleblue flex-shrink-0">
              <img className="prof-init-icon" src={UserIcon} />
            </div>
            <div className="introduction-content flex-grow-1 ml-10 pe-0 ps-0 pb-0">
              <p className="mb-0 notification-head">
                <span className="f-bold">
                  {notification?.Want_ad_Read_Count}{" "}
                  {notification?.Want_ad_Read_Count > 1 ? "members" : "member"}
                </span>{" "}
                viewed your want-listing{" "}
                <span className="f-bold">{notification?.wantAdTitle}</span>
                <span className="time-stamp">
                  {notification?.LastUpdatedTS &&
                    timeAgo(
                      moment.utc(notification?.LastUpdatedTS).toISOString(),
                    )}
                </span>
              </p>
            </div>
          </div>
          {notification?.Read_Status == 1 && (
            <div className="dot-indicator dot-bg-red align-self-center mt-0"></div>
          )}
        </div>
      </div>
    );
  }
  /** Notification for want ad count based on location */

  function RenderWantAdCountBasedOnLocationNotification({ notification }) {
    return (
      <div
        className="outer-property-wrap pl-20 pr-20 pt-12 pb-12 notify-info-bg notification-sec position-relative pointer"
        role="button"
        onClick={(e) => {
          e.preventDefault();
          redirectHomePageBasedOnLocationSearch(
            notification?.City + "," + notification?.State,
          );
        }}
        onKeyDown={(e) => {
          e.preventDefault();
          redirectHomePageBasedOnLocationSearch(
            notification?.City + "," + notification?.State,
          );
        }}
      >
        <button
          className="btn notif-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            updateInAppDeleteStatusOnClick(
              _.get(notification, "NotificationId", ""),
              notification?.SubjectType,
              true,
              1,
            );
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.9999 2C10.2458 2 8.78514 3.31072 8.53998 5H5.11908C5.0765 4.99271 5.03337 4.98912 4.99017 4.98926C4.95285 4.99006 4.91564 4.99365 4.87885 5H3.24994C3.15056 4.99859 3.05188 5.01696 2.95966 5.05402C2.86743 5.09108 2.78349 5.1461 2.71271 5.21588C2.64194 5.28566 2.58573 5.36882 2.54737 5.46051C2.50901 5.5522 2.48926 5.65061 2.48926 5.75C2.48926 5.84939 2.50901 5.9478 2.54737 6.03949C2.58573 6.13118 2.64194 6.21434 2.71271 6.28412C2.78349 6.3539 2.86743 6.40892 2.95966 6.44598C3.05188 6.48304 3.15056 6.50141 3.24994 6.5H4.31928L5.57806 19.5146C5.7136 20.918 6.90583 22 8.31537 22H15.6835C17.0931 22 18.2854 20.9181 18.4208 19.5146L19.6806 6.5H20.7499C20.8493 6.50141 20.948 6.48304 21.0402 6.44598C21.1324 6.40892 21.2164 6.3539 21.2872 6.28412C21.3579 6.21434 21.4141 6.13118 21.4525 6.03949C21.4909 5.9478 21.5106 5.84939 21.5106 5.75C21.5106 5.65061 21.4909 5.5522 21.4525 5.46051C21.4141 5.36882 21.3579 5.28566 21.2872 5.21588C21.2164 5.1461 21.1324 5.09108 21.0402 5.05402C20.948 5.01696 20.8493 4.99859 20.7499 5H19.122C19.0425 4.98709 18.9613 4.98709 18.8818 5H15.4599C15.2147 3.31072 13.7541 2 11.9999 2ZM11.9999 3.5C12.9395 3.5 13.7103 4.13408 13.9306 5H10.0693C10.2896 4.13408 11.0604 3.5 11.9999 3.5ZM5.82513 6.5H18.1738L16.9277 19.3701C16.8652 20.0177 16.3339 20.5 15.6835 20.5H8.31537C7.66591 20.5 7.13369 20.0168 7.07123 19.3701L5.82513 6.5ZM10.2382 8.98926C10.0395 8.99236 9.8501 9.07423 9.71167 9.21686C9.57324 9.3595 9.49709 9.55125 9.49994 9.75V17.25C9.49853 17.3494 9.5169 17.4481 9.55396 17.5403C9.59102 17.6325 9.64604 17.7164 9.71582 17.7872C9.7856 17.858 9.86876 17.9142 9.96045 17.9526C10.0521 17.9909 10.1505 18.0107 10.2499 18.0107C10.3493 18.0107 10.4477 17.9909 10.5394 17.9526C10.6311 17.9142 10.7143 17.858 10.7841 17.7872C10.8538 17.7164 10.9089 17.6325 10.9459 17.5403C10.983 17.4481 11.0013 17.3494 10.9999 17.25V9.75C11.0014 9.64962 10.9827 9.54997 10.9449 9.45695C10.9071 9.36394 10.851 9.27946 10.78 9.20852C10.709 9.13758 10.6244 9.08161 10.5313 9.04395C10.4383 9.00629 10.3386 8.98769 10.2382 8.98926ZM13.7382 8.98926C13.5395 8.99236 13.3501 9.07423 13.2117 9.21686C13.0732 9.3595 12.9971 9.55125 12.9999 9.75V17.25C12.9985 17.3494 13.0169 17.4481 13.054 17.5403C13.091 17.6325 13.146 17.7164 13.2158 17.7872C13.2856 17.858 13.3688 17.9142 13.4605 17.9526C13.5521 17.9909 13.6505 18.0107 13.7499 18.0107C13.8493 18.0107 13.9477 17.9909 14.0394 17.9526C14.1311 17.9142 14.2143 17.858 14.2841 17.7872C14.3538 17.7164 14.4089 17.6325 14.4459 17.5403C14.483 17.4481 14.5013 17.3494 14.4999 17.25V9.75C14.5014 9.64962 14.4827 9.54997 14.4449 9.45695C14.4071 9.36394 14.351 9.27946 14.28 9.20852C14.209 9.13758 14.1244 9.08161 14.0313 9.04395C13.9383 9.00629 13.8386 8.98769 13.7382 8.98926Z"
              fill={themeColors.neutral890}
            />
          </svg>
        </button>
        <div className="d-flex mb-0 flex-grow-1 align-items-center">
          <div className="intro-prof-wrap profile-bg-turquoise flex-shrink-0">
            <img className="prof-init-icon" src={ProfInitialsIcon} />
          </div>
          <div className="introduction-content flex-grow-1 ml-10 pe-0 ps-0 pb-0">
            <p className="mb-0 notification-head">
              <span className="f-bold">
                {notification?.Count} New Want-Listings
              </span>{" "}
              have been posted in {notification?.City}, {notification?.State} in
              the last 24 hours. Take a look!{" "}
              <span className="time-stamp-grey">
                {notification?.LastUpdatedTS &&
                  timeAgo(
                    moment.utc(notification?.LastUpdatedTS).toISOString(),
                  )}
              </span>
            </p>
          </div>
          {notification?.Read_Status == 1 && (
            <div className="dot-indicator dot-bg-red align-self-center mt-0"></div>
          )}
        </div>
      </div>
    );
  }
  /** Notification for matches want ads */

  function RenderMatchNotification({ notification }) {
    return (
      <div
        className="outer-property-wrap pl-20 pr-20 pt-12 pb-12 notify-info-bg notification-sec position-relative pointer"
        role="button"
        onClick={(e) => {
          e.stopPropagation();
          navigate((notification?.url).replace(/^.*\/\/[^\/]+/, ""));
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          navigate((notification?.url).replace(/^.*\/\/[^\/]+/, ""));
        }}
      >
        <button
          className="btn notif-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            updateInAppDeleteStatusOnClick(
              _.get(notification, "NotificationId", ""),
              notification?.SubjectType,
              true,
              1,
            );
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.9999 2C10.2458 2 8.78514 3.31072 8.53998 5H5.11908C5.0765 4.99271 5.03337 4.98912 4.99017 4.98926C4.95285 4.99006 4.91564 4.99365 4.87885 5H3.24994C3.15056 4.99859 3.05188 5.01696 2.95966 5.05402C2.86743 5.09108 2.78349 5.1461 2.71271 5.21588C2.64194 5.28566 2.58573 5.36882 2.54737 5.46051C2.50901 5.5522 2.48926 5.65061 2.48926 5.75C2.48926 5.84939 2.50901 5.9478 2.54737 6.03949C2.58573 6.13118 2.64194 6.21434 2.71271 6.28412C2.78349 6.3539 2.86743 6.40892 2.95966 6.44598C3.05188 6.48304 3.15056 6.50141 3.24994 6.5H4.31928L5.57806 19.5146C5.7136 20.918 6.90583 22 8.31537 22H15.6835C17.0931 22 18.2854 20.9181 18.4208 19.5146L19.6806 6.5H20.7499C20.8493 6.50141 20.948 6.48304 21.0402 6.44598C21.1324 6.40892 21.2164 6.3539 21.2872 6.28412C21.3579 6.21434 21.4141 6.13118 21.4525 6.03949C21.4909 5.9478 21.5106 5.84939 21.5106 5.75C21.5106 5.65061 21.4909 5.5522 21.4525 5.46051C21.4141 5.36882 21.3579 5.28566 21.2872 5.21588C21.2164 5.1461 21.1324 5.09108 21.0402 5.05402C20.948 5.01696 20.8493 4.99859 20.7499 5H19.122C19.0425 4.98709 18.9613 4.98709 18.8818 5H15.4599C15.2147 3.31072 13.7541 2 11.9999 2ZM11.9999 3.5C12.9395 3.5 13.7103 4.13408 13.9306 5H10.0693C10.2896 4.13408 11.0604 3.5 11.9999 3.5ZM5.82513 6.5H18.1738L16.9277 19.3701C16.8652 20.0177 16.3339 20.5 15.6835 20.5H8.31537C7.66591 20.5 7.13369 20.0168 7.07123 19.3701L5.82513 6.5ZM10.2382 8.98926C10.0395 8.99236 9.8501 9.07423 9.71167 9.21686C9.57324 9.3595 9.49709 9.55125 9.49994 9.75V17.25C9.49853 17.3494 9.5169 17.4481 9.55396 17.5403C9.59102 17.6325 9.64604 17.7164 9.71582 17.7872C9.7856 17.858 9.86876 17.9142 9.96045 17.9526C10.0521 17.9909 10.1505 18.0107 10.2499 18.0107C10.3493 18.0107 10.4477 17.9909 10.5394 17.9526C10.6311 17.9142 10.7143 17.858 10.7841 17.7872C10.8538 17.7164 10.9089 17.6325 10.9459 17.5403C10.983 17.4481 11.0013 17.3494 10.9999 17.25V9.75C11.0014 9.64962 10.9827 9.54997 10.9449 9.45695C10.9071 9.36394 10.851 9.27946 10.78 9.20852C10.709 9.13758 10.6244 9.08161 10.5313 9.04395C10.4383 9.00629 10.3386 8.98769 10.2382 8.98926ZM13.7382 8.98926C13.5395 8.99236 13.3501 9.07423 13.2117 9.21686C13.0732 9.3595 12.9971 9.55125 12.9999 9.75V17.25C12.9985 17.3494 13.0169 17.4481 13.054 17.5403C13.091 17.6325 13.146 17.7164 13.2158 17.7872C13.2856 17.858 13.3688 17.9142 13.4605 17.9526C13.5521 17.9909 13.6505 18.0107 13.7499 18.0107C13.8493 18.0107 13.9477 17.9909 14.0394 17.9526C14.1311 17.9142 14.2143 17.858 14.2841 17.7872C14.3538 17.7164 14.4089 17.6325 14.4459 17.5403C14.483 17.4481 14.5013 17.3494 14.4999 17.25V9.75C14.5014 9.64962 14.4827 9.54997 14.4449 9.45695C14.4071 9.36394 14.351 9.27946 14.28 9.20852C14.209 9.13758 14.1244 9.08161 14.0313 9.04395C13.9383 9.00629 13.8386 8.98769 13.7382 8.98926Z"
              fill={themeColors.neutral890}
            />
          </svg>
        </button>
        <div className="d-flex mb-0 flex-grow-1 align-items-center">
          <div className="intro-prof-wrap profile-bg-yellow-turmeric flex-shrink-0">
            <img className="prof-init-icon" src={ConfettiIcon} />
          </div>
          <div className="introduction-content flex-grow-1 ml-10 pe-0 ps-0 pb-0">
            <p className="mb-0 notification-head">
              <span className="f-bold">{notification?.Count} New Matches </span>{" "}
              have been found for{" "}
              <span className="f-bold"> {notification?.Location} </span>. Make
              an introduction today!
              <span className="time-stamp-grey">
                {notification?.LastUpdatedTS &&
                  timeAgo(
                    moment.utc(notification?.LastUpdatedTS).toISOString(),
                  )}
              </span>
            </p>
          </div>
          {notification?.Read_Status == 1 && (
            <div className="dot-indicator dot-bg-red align-self-center mt-0"></div>
          )}
        </div>
      </div>
    );
  }

  /** Notification for  direct messages view */

  const checkIsMobileView = () => {
    if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      )
    ) {
      return true;
    } else false;
  };

  function RenderDMNotification({ notification }) {
    const SenderId = _.get(notification, "SenderId", null);
    const userFirstName = _.get(notification, "userFirstName", null);
    const userLastName = _.get(notification, "userLastName", null);
    const userImage = _.get(notification, "userImage", null);
    const LastUpdatedTS = _.get(notification, "LastUpdatedTS", 1);
    let navigationUrl = `/direct-messages?id=${SenderId}`;
    if (checkIsMobileView()) {
      navigationUrl = `/mobile/direct-messages?id=${SenderId}`;
    }

    const handleDMNavigation = () => {
      redirectToBLSEnv({
        path: navigationUrl,
        onGitchaEnvFallback: () => {
          navigate(navigationUrl);
        },
      });
    };

    return (
      <div
        className="notification-sec position-relative pointer"
        role="button"
        onClick={(e) => {
          e.stopPropagation();
          handleDMNavigation();
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          handleDMNavigation();
        }}
      >
        <button
          className="btn notif-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            updateInAppDeleteStatusOnClick(
              _.get(notification, "NotificationId", ""),
              notification?.SubjectType,
              true,
              1,
            );
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.9999 2C10.2458 2 8.78514 3.31072 8.53998 5H5.11908C5.0765 4.99271 5.03337 4.98912 4.99017 4.98926C4.95285 4.99006 4.91564 4.99365 4.87885 5H3.24994C3.15056 4.99859 3.05188 5.01696 2.95966 5.05402C2.86743 5.09108 2.78349 5.1461 2.71271 5.21588C2.64194 5.28566 2.58573 5.36882 2.54737 5.46051C2.50901 5.5522 2.48926 5.65061 2.48926 5.75C2.48926 5.84939 2.50901 5.9478 2.54737 6.03949C2.58573 6.13118 2.64194 6.21434 2.71271 6.28412C2.78349 6.3539 2.86743 6.40892 2.95966 6.44598C3.05188 6.48304 3.15056 6.50141 3.24994 6.5H4.31928L5.57806 19.5146C5.7136 20.918 6.90583 22 8.31537 22H15.6835C17.0931 22 18.2854 20.9181 18.4208 19.5146L19.6806 6.5H20.7499C20.8493 6.50141 20.948 6.48304 21.0402 6.44598C21.1324 6.40892 21.2164 6.3539 21.2872 6.28412C21.3579 6.21434 21.4141 6.13118 21.4525 6.03949C21.4909 5.9478 21.5106 5.84939 21.5106 5.75C21.5106 5.65061 21.4909 5.5522 21.4525 5.46051C21.4141 5.36882 21.3579 5.28566 21.2872 5.21588C21.2164 5.1461 21.1324 5.09108 21.0402 5.05402C20.948 5.01696 20.8493 4.99859 20.7499 5H19.122C19.0425 4.98709 18.9613 4.98709 18.8818 5H15.4599C15.2147 3.31072 13.7541 2 11.9999 2ZM11.9999 3.5C12.9395 3.5 13.7103 4.13408 13.9306 5H10.0693C10.2896 4.13408 11.0604 3.5 11.9999 3.5ZM5.82513 6.5H18.1738L16.9277 19.3701C16.8652 20.0177 16.3339 20.5 15.6835 20.5H8.31537C7.66591 20.5 7.13369 20.0168 7.07123 19.3701L5.82513 6.5ZM10.2382 8.98926C10.0395 8.99236 9.8501 9.07423 9.71167 9.21686C9.57324 9.3595 9.49709 9.55125 9.49994 9.75V17.25C9.49853 17.3494 9.5169 17.4481 9.55396 17.5403C9.59102 17.6325 9.64604 17.7164 9.71582 17.7872C9.7856 17.858 9.86876 17.9142 9.96045 17.9526C10.0521 17.9909 10.1505 18.0107 10.2499 18.0107C10.3493 18.0107 10.4477 17.9909 10.5394 17.9526C10.6311 17.9142 10.7143 17.858 10.7841 17.7872C10.8538 17.7164 10.9089 17.6325 10.9459 17.5403C10.983 17.4481 11.0013 17.3494 10.9999 17.25V9.75C11.0014 9.64962 10.9827 9.54997 10.9449 9.45695C10.9071 9.36394 10.851 9.27946 10.78 9.20852C10.709 9.13758 10.6244 9.08161 10.5313 9.04395C10.4383 9.00629 10.3386 8.98769 10.2382 8.98926ZM13.7382 8.98926C13.5395 8.99236 13.3501 9.07423 13.2117 9.21686C13.0732 9.3595 12.9971 9.55125 12.9999 9.75V17.25C12.9985 17.3494 13.0169 17.4481 13.054 17.5403C13.091 17.6325 13.146 17.7164 13.2158 17.7872C13.2856 17.858 13.3688 17.9142 13.4605 17.9526C13.5521 17.9909 13.6505 18.0107 13.7499 18.0107C13.8493 18.0107 13.9477 17.9909 14.0394 17.9526C14.1311 17.9142 14.2143 17.858 14.2841 17.7872C14.3538 17.7164 14.4089 17.6325 14.4459 17.5403C14.483 17.4481 14.5013 17.3494 14.4999 17.25V9.75C14.5014 9.64962 14.4827 9.54997 14.4449 9.45695C14.4071 9.36394 14.351 9.27946 14.28 9.20852C14.209 9.13758 14.1244 9.08161 14.0313 9.04395C13.9383 9.00629 13.8386 8.98769 13.7382 8.98926Z"
              fill={themeColors.neutral890}
            />
          </svg>
        </button>
        <div className="outer-property-wrap pl-20 pr-20 pt-12 pb-12">
          <div className="d-flex mb-0 flex-grow-1 align-items-center">
            <div className="intro-prof-wrap profile-bg-paleblue flex-shrink-0">
              <div className="msg-prof-wrap flex-shrink-0">
                <img src={userImage || UserDefaultIcon} alt="User" />
              </div>
            </div>
            <div className="introduction-content flex-grow-1 ml-10 pe-0 ps-0 pb-0">
              <p className="mb-0 notification-head">
                You received a new message from{" "}
                <span className="f-bold">
                  {userFirstName} {userLastName}
                </span>
                <span className="time-stamp">
                  {LastUpdatedTS &&
                    timeAgo(moment.utc(LastUpdatedTS).toISOString())}
                </span>
              </p>
            </div>
          </div>
          {notification?.Read_Status == 1 && (
            <div className="dot-indicator dot-bg-red align-self-center mt-0"></div>
          )}
        </div>
      </div>
    );
  }

  /**
   * get inventory details for edit
   * @param {*} inventoryId
   */
  const getInventoryDetails = async (inventoryId) => {
    if (inventoryId) {
      await getInventoryById(inventoryId)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            if (responseResult.isAgentCreated === 1) {
              const path = `${pageURLs.agentCreateInventory}?inventoryId=${responseResult.inventoryId}`;
              redirectToBLSEnv({
                path,
                onGitchaEnvFallback: () => {
                  navigate(pageURLs.agentCreateInventory, {
                    state: {
                      data: responseResult,
                      inventoryId: responseResult.inventoryId,
                    },
                  });
                },
              });
            } else {
              navigate(pageURLs.memberEditInventory, {
                state: { data: responseResult },
              });
            }
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    } else {
      console.log("Invalid inventory id");
    }
  };
  /** Notification for invalid image uploaded against inventory */

  function RenderImageInvalidNotification({ notification }) {
    return (
      <div
        className="outer-property-wrap pl-20 pr-20 pt-12 pb-12 notify-info-bg notification-sec position-relative pointer"
        role="button"
        onClick={(e) => {
          e.stopPropagation();
          getInventoryDetails(notification?.Inv_Id);
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          getInventoryDetails(notification?.Inv_Id);
        }}
      >
        <button
          className="btn notif-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            updateInAppDeleteStatusOnClick(
              _.get(notification, "NotificationId", ""),
              notification?.SubjectType,
              true,
              1,
            );
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.9999 2C10.2458 2 8.78514 3.31072 8.53998 5H5.11908C5.0765 4.99271 5.03337 4.98912 4.99017 4.98926C4.95285 4.99006 4.91564 4.99365 4.87885 5H3.24994C3.15056 4.99859 3.05188 5.01696 2.95966 5.05402C2.86743 5.09108 2.78349 5.1461 2.71271 5.21588C2.64194 5.28566 2.58573 5.36882 2.54737 5.46051C2.50901 5.5522 2.48926 5.65061 2.48926 5.75C2.48926 5.84939 2.50901 5.9478 2.54737 6.03949C2.58573 6.13118 2.64194 6.21434 2.71271 6.28412C2.78349 6.3539 2.86743 6.40892 2.95966 6.44598C3.05188 6.48304 3.15056 6.50141 3.24994 6.5H4.31928L5.57806 19.5146C5.7136 20.918 6.90583 22 8.31537 22H15.6835C17.0931 22 18.2854 20.9181 18.4208 19.5146L19.6806 6.5H20.7499C20.8493 6.50141 20.948 6.48304 21.0402 6.44598C21.1324 6.40892 21.2164 6.3539 21.2872 6.28412C21.3579 6.21434 21.4141 6.13118 21.4525 6.03949C21.4909 5.9478 21.5106 5.84939 21.5106 5.75C21.5106 5.65061 21.4909 5.5522 21.4525 5.46051C21.4141 5.36882 21.3579 5.28566 21.2872 5.21588C21.2164 5.1461 21.1324 5.09108 21.0402 5.05402C20.948 5.01696 20.8493 4.99859 20.7499 5H19.122C19.0425 4.98709 18.9613 4.98709 18.8818 5H15.4599C15.2147 3.31072 13.7541 2 11.9999 2ZM11.9999 3.5C12.9395 3.5 13.7103 4.13408 13.9306 5H10.0693C10.2896 4.13408 11.0604 3.5 11.9999 3.5ZM5.82513 6.5H18.1738L16.9277 19.3701C16.8652 20.0177 16.3339 20.5 15.6835 20.5H8.31537C7.66591 20.5 7.13369 20.0168 7.07123 19.3701L5.82513 6.5ZM10.2382 8.98926C10.0395 8.99236 9.8501 9.07423 9.71167 9.21686C9.57324 9.3595 9.49709 9.55125 9.49994 9.75V17.25C9.49853 17.3494 9.5169 17.4481 9.55396 17.5403C9.59102 17.6325 9.64604 17.7164 9.71582 17.7872C9.7856 17.858 9.86876 17.9142 9.96045 17.9526C10.0521 17.9909 10.1505 18.0107 10.2499 18.0107C10.3493 18.0107 10.4477 17.9909 10.5394 17.9526C10.6311 17.9142 10.7143 17.858 10.7841 17.7872C10.8538 17.7164 10.9089 17.6325 10.9459 17.5403C10.983 17.4481 11.0013 17.3494 10.9999 17.25V9.75C11.0014 9.64962 10.9827 9.54997 10.9449 9.45695C10.9071 9.36394 10.851 9.27946 10.78 9.20852C10.709 9.13758 10.6244 9.08161 10.5313 9.04395C10.4383 9.00629 10.3386 8.98769 10.2382 8.98926ZM13.7382 8.98926C13.5395 8.99236 13.3501 9.07423 13.2117 9.21686C13.0732 9.3595 12.9971 9.55125 12.9999 9.75V17.25C12.9985 17.3494 13.0169 17.4481 13.054 17.5403C13.091 17.6325 13.146 17.7164 13.2158 17.7872C13.2856 17.858 13.3688 17.9142 13.4605 17.9526C13.5521 17.9909 13.6505 18.0107 13.7499 18.0107C13.8493 18.0107 13.9477 17.9909 14.0394 17.9526C14.1311 17.9142 14.2143 17.858 14.2841 17.7872C14.3538 17.7164 14.4089 17.6325 14.4459 17.5403C14.483 17.4481 14.5013 17.3494 14.4999 17.25V9.75C14.5014 9.64962 14.4827 9.54997 14.4449 9.45695C14.4071 9.36394 14.351 9.27946 14.28 9.20852C14.209 9.13758 14.1244 9.08161 14.0313 9.04395C13.9383 9.00629 13.8386 8.98769 13.7382 8.98926Z"
              fill={themeColors.neutral890}
            />
          </svg>
        </button>
        <div className="d-flex mb-0 flex-grow-1 align-items-center">
          <div className="intro-prof-wrap profile-bg-darkmeroon flex-shrink-0">
            <img className="prof-init-icon" src={HomeIcon} />
          </div>
          <div className="introduction-content flex-grow-1 ml-10 pe-0 ps-0 pb-0">
            <p className="mb-0 notification-head">
              Image uploaded against{" "}
              <span className="f-bold"> {notification?.Location} </span> was
              marked "invalid". Reason: {notification?.Reason_Text}
              <span className="time-stamp-grey">
                {notification?.LastUpdatedTS &&
                  timeAgo(
                    moment.utc(notification?.LastUpdatedTS).toISOString(),
                  )}
              </span>
            </p>
          </div>
          {notification?.Read_Status == 1 && (
            <div className="dot-indicator dot-bg-red align-self-center mt-0"></div>
          )}
        </div>
      </div>
    );
  }
  /** Notification for introduction accepted */

  function RenderIntroductionAcceptedNotification({ notification }) {
    return (
      <div
        className="notification-sec position-relative notify-info-bg pointer"
        role="button"
        onClick={(e) => {
          viewIntroductions(
            _.get(notification, "Want_ad_Id", ""),
            _.get(notification, "slug", ""),
            null,
          );
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          viewIntroductions(
            _.get(notification, "Want_ad_Id", ""),
            _.get(notification, "slug", ""),
            null,
          );
          e.stopPropagation();
        }}
      >
        <button
          className="btn notif-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            updateInAppDeleteStatusOnClick(
              _.get(notification, "NotificationId", ""),
              notification?.SubjectType,
              true,
              1,
            );
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.9999 2C10.2458 2 8.78514 3.31072 8.53998 5H5.11908C5.0765 4.99271 5.03337 4.98912 4.99017 4.98926C4.95285 4.99006 4.91564 4.99365 4.87885 5H3.24994C3.15056 4.99859 3.05188 5.01696 2.95966 5.05402C2.86743 5.09108 2.78349 5.1461 2.71271 5.21588C2.64194 5.28566 2.58573 5.36882 2.54737 5.46051C2.50901 5.5522 2.48926 5.65061 2.48926 5.75C2.48926 5.84939 2.50901 5.9478 2.54737 6.03949C2.58573 6.13118 2.64194 6.21434 2.71271 6.28412C2.78349 6.3539 2.86743 6.40892 2.95966 6.44598C3.05188 6.48304 3.15056 6.50141 3.24994 6.5H4.31928L5.57806 19.5146C5.7136 20.918 6.90583 22 8.31537 22H15.6835C17.0931 22 18.2854 20.9181 18.4208 19.5146L19.6806 6.5H20.7499C20.8493 6.50141 20.948 6.48304 21.0402 6.44598C21.1324 6.40892 21.2164 6.3539 21.2872 6.28412C21.3579 6.21434 21.4141 6.13118 21.4525 6.03949C21.4909 5.9478 21.5106 5.84939 21.5106 5.75C21.5106 5.65061 21.4909 5.5522 21.4525 5.46051C21.4141 5.36882 21.3579 5.28566 21.2872 5.21588C21.2164 5.1461 21.1324 5.09108 21.0402 5.05402C20.948 5.01696 20.8493 4.99859 20.7499 5H19.122C19.0425 4.98709 18.9613 4.98709 18.8818 5H15.4599C15.2147 3.31072 13.7541 2 11.9999 2ZM11.9999 3.5C12.9395 3.5 13.7103 4.13408 13.9306 5H10.0693C10.2896 4.13408 11.0604 3.5 11.9999 3.5ZM5.82513 6.5H18.1738L16.9277 19.3701C16.8652 20.0177 16.3339 20.5 15.6835 20.5H8.31537C7.66591 20.5 7.13369 20.0168 7.07123 19.3701L5.82513 6.5ZM10.2382 8.98926C10.0395 8.99236 9.8501 9.07423 9.71167 9.21686C9.57324 9.3595 9.49709 9.55125 9.49994 9.75V17.25C9.49853 17.3494 9.5169 17.4481 9.55396 17.5403C9.59102 17.6325 9.64604 17.7164 9.71582 17.7872C9.7856 17.858 9.86876 17.9142 9.96045 17.9526C10.0521 17.9909 10.1505 18.0107 10.2499 18.0107C10.3493 18.0107 10.4477 17.9909 10.5394 17.9526C10.6311 17.9142 10.7143 17.858 10.7841 17.7872C10.8538 17.7164 10.9089 17.6325 10.9459 17.5403C10.983 17.4481 11.0013 17.3494 10.9999 17.25V9.75C11.0014 9.64962 10.9827 9.54997 10.9449 9.45695C10.9071 9.36394 10.851 9.27946 10.78 9.20852C10.709 9.13758 10.6244 9.08161 10.5313 9.04395C10.4383 9.00629 10.3386 8.98769 10.2382 8.98926ZM13.7382 8.98926C13.5395 8.99236 13.3501 9.07423 13.2117 9.21686C13.0732 9.3595 12.9971 9.55125 12.9999 9.75V17.25C12.9985 17.3494 13.0169 17.4481 13.054 17.5403C13.091 17.6325 13.146 17.7164 13.2158 17.7872C13.2856 17.858 13.3688 17.9142 13.4605 17.9526C13.5521 17.9909 13.6505 18.0107 13.7499 18.0107C13.8493 18.0107 13.9477 17.9909 14.0394 17.9526C14.1311 17.9142 14.2143 17.858 14.2841 17.7872C14.3538 17.7164 14.4089 17.6325 14.4459 17.5403C14.483 17.4481 14.5013 17.3494 14.4999 17.25V9.75C14.5014 9.64962 14.4827 9.54997 14.4449 9.45695C14.4071 9.36394 14.351 9.27946 14.28 9.20852C14.209 9.13758 14.1244 9.08161 14.0313 9.04395C13.9383 9.00629 13.8386 8.98769 13.7382 8.98926Z"
              fill={themeColors.neutral890}
            />
          </svg>
        </button>
        <div className="outer-property-wrap pl-20 pr-20 pt-12 pb-12">
          <div className="d-flex mb-0 flex-grow-1 align-items-center">
            <div className="intro-prof-wrap profile-bg-darkmeroon flex-shrink-0">
              <img className="prof-init-icon" src={NotifHandshakeIcon} />
            </div>
            <div className="introduction-content flex-grow-1 ml-10 pe-0 ps-0 pb-0">
              {notification?.IntroductionType == 1 ? (
                <p className="mb-0 notification-head">
                  Your introduction for the want-listing{" "}
                  <span className="f-bold">{notification?.wantAdTitle}</span>{" "}
                  has been accepted.{" "}
                  <span className="time-stamp">
                    {notification?.LastUpdatedTS &&
                      timeAgo(
                        moment.utc(notification?.LastUpdatedTS).toISOString(),
                      )}
                  </span>
                </p>
              ) : (
                <p className="mb-0 notification-head">
                  Your introduction sent to want-listing{" "}
                  <span className="f-bold">{notification?.wantAdTitle}</span>{" "}
                  was marked “not a fit.”
                  {notification?.markreason.toString() &&
                    " Reason: " +
                      notification?.markreason.toString().replace(/,/g, ", ")}
                  {notification?.Reason_Text &&
                    ', "' + notification?.Reason_Text + '"'}
                  <span className="time-stamp">
                    {notification?.LastUpdatedTS &&
                      timeAgo(
                        moment.utc(notification?.LastUpdatedTS).toISOString(),
                      )}
                  </span>
                </p>
              )}
            </div>
          </div>
          {notification?.Read_Status == 1 && (
            <div className="dot-indicator dot-bg-red align-self-center mt-0"></div>
          )}
        </div>
      </div>
    );
  }
  /** Notification for  want ad expired */

  function RenderWantadExpireNotification({ notification }) {
    return (
      <div
        className="notification-sec position-relative notify-info-bg pointer"
        role="button"
        onClick={(e) => {
          e.preventDefault();
          renewWantAd(notification);
        }}
        onKeyDown={(e) => {
          e.preventDefault();
          renewWantAd(notification);
        }}
      >
        <button
          className="btn notif-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            updateInAppDeleteStatusOnClick(
              _.get(notification, "NotificationId", ""),
              notification?.SubjectType,
              true,
              1,
            );
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.9999 2C10.2458 2 8.78514 3.31072 8.53998 5H5.11908C5.0765 4.99271 5.03337 4.98912 4.99017 4.98926C4.95285 4.99006 4.91564 4.99365 4.87885 5H3.24994C3.15056 4.99859 3.05188 5.01696 2.95966 5.05402C2.86743 5.09108 2.78349 5.1461 2.71271 5.21588C2.64194 5.28566 2.58573 5.36882 2.54737 5.46051C2.50901 5.5522 2.48926 5.65061 2.48926 5.75C2.48926 5.84939 2.50901 5.9478 2.54737 6.03949C2.58573 6.13118 2.64194 6.21434 2.71271 6.28412C2.78349 6.3539 2.86743 6.40892 2.95966 6.44598C3.05188 6.48304 3.15056 6.50141 3.24994 6.5H4.31928L5.57806 19.5146C5.7136 20.918 6.90583 22 8.31537 22H15.6835C17.0931 22 18.2854 20.9181 18.4208 19.5146L19.6806 6.5H20.7499C20.8493 6.50141 20.948 6.48304 21.0402 6.44598C21.1324 6.40892 21.2164 6.3539 21.2872 6.28412C21.3579 6.21434 21.4141 6.13118 21.4525 6.03949C21.4909 5.9478 21.5106 5.84939 21.5106 5.75C21.5106 5.65061 21.4909 5.5522 21.4525 5.46051C21.4141 5.36882 21.3579 5.28566 21.2872 5.21588C21.2164 5.1461 21.1324 5.09108 21.0402 5.05402C20.948 5.01696 20.8493 4.99859 20.7499 5H19.122C19.0425 4.98709 18.9613 4.98709 18.8818 5H15.4599C15.2147 3.31072 13.7541 2 11.9999 2ZM11.9999 3.5C12.9395 3.5 13.7103 4.13408 13.9306 5H10.0693C10.2896 4.13408 11.0604 3.5 11.9999 3.5ZM5.82513 6.5H18.1738L16.9277 19.3701C16.8652 20.0177 16.3339 20.5 15.6835 20.5H8.31537C7.66591 20.5 7.13369 20.0168 7.07123 19.3701L5.82513 6.5ZM10.2382 8.98926C10.0395 8.99236 9.8501 9.07423 9.71167 9.21686C9.57324 9.3595 9.49709 9.55125 9.49994 9.75V17.25C9.49853 17.3494 9.5169 17.4481 9.55396 17.5403C9.59102 17.6325 9.64604 17.7164 9.71582 17.7872C9.7856 17.858 9.86876 17.9142 9.96045 17.9526C10.0521 17.9909 10.1505 18.0107 10.2499 18.0107C10.3493 18.0107 10.4477 17.9909 10.5394 17.9526C10.6311 17.9142 10.7143 17.858 10.7841 17.7872C10.8538 17.7164 10.9089 17.6325 10.9459 17.5403C10.983 17.4481 11.0013 17.3494 10.9999 17.25V9.75C11.0014 9.64962 10.9827 9.54997 10.9449 9.45695C10.9071 9.36394 10.851 9.27946 10.78 9.20852C10.709 9.13758 10.6244 9.08161 10.5313 9.04395C10.4383 9.00629 10.3386 8.98769 10.2382 8.98926ZM13.7382 8.98926C13.5395 8.99236 13.3501 9.07423 13.2117 9.21686C13.0732 9.3595 12.9971 9.55125 12.9999 9.75V17.25C12.9985 17.3494 13.0169 17.4481 13.054 17.5403C13.091 17.6325 13.146 17.7164 13.2158 17.7872C13.2856 17.858 13.3688 17.9142 13.4605 17.9526C13.5521 17.9909 13.6505 18.0107 13.7499 18.0107C13.8493 18.0107 13.9477 17.9909 14.0394 17.9526C14.1311 17.9142 14.2143 17.858 14.2841 17.7872C14.3538 17.7164 14.4089 17.6325 14.4459 17.5403C14.483 17.4481 14.5013 17.3494 14.4999 17.25V9.75C14.5014 9.64962 14.4827 9.54997 14.4449 9.45695C14.4071 9.36394 14.351 9.27946 14.28 9.20852C14.209 9.13758 14.1244 9.08161 14.0313 9.04395C13.9383 9.00629 13.8386 8.98769 13.7382 8.98926Z"
              fill={themeColors.neutral890}
            />
          </svg>
        </button>
        <div className="outer-property-wrap pl-20 pr-20 pt-12 pb-12">
          <div className="d-flex mb-0 flex-grow-1 align-items-center">
            <div className="intro-prof-wrap profile-bg-darkmeroon flex-shrink-0">
              <img className="prof-init-icon" src={NotifHandshakeIcon} />
            </div>
            <div className="introduction-content flex-grow-1 ml-10 pe-0 ps-0 pb-0">
              <p className="mb-0 notification-head">
                Your want-listing{" "}
                <span className="f-bold">{notification?.Want_ad_Title}</span>{" "}
                Expires Soon on{" "}
                {formateDate(notification?.ExpiryDate, "mm/dd/yyyy")}. Click
                here to renew! If not, we’ll store it in your “Paused” folder
                just in case you decided to post it again.
                <span className="time-stamp">
                  {notification?.LastUpdatedTS &&
                    timeAgo(
                      moment.utc(notification?.LastUpdatedTS).toISOString(),
                    )}
                </span>
              </p>
            </div>
          </div>
          {notification?.Read_Status == 1 && (
            <div className="dot-indicator dot-bg-red align-self-center mt-0"></div>
          )}
        </div>
      </div>
    );
  }
  /** Notification for client completed form link want ad */

  function RenderBuyerListWantAdNotification({ notification }) {
    return (
      <div className="notification-sec position-relative notify-info-bg pointer">
        <button
          className="btn notif-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            updateInAppDeleteStatusOnClick(
              _.get(notification, "NotificationId", ""),
              notification?.SubjectType,
              true,
              1,
            );
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.9999 2C10.2458 2 8.78514 3.31072 8.53998 5H5.11908C5.0765 4.99271 5.03337 4.98912 4.99017 4.98926C4.95285 4.99006 4.91564 4.99365 4.87885 5H3.24994C3.15056 4.99859 3.05188 5.01696 2.95966 5.05402C2.86743 5.09108 2.78349 5.1461 2.71271 5.21588C2.64194 5.28566 2.58573 5.36882 2.54737 5.46051C2.50901 5.5522 2.48926 5.65061 2.48926 5.75C2.48926 5.84939 2.50901 5.9478 2.54737 6.03949C2.58573 6.13118 2.64194 6.21434 2.71271 6.28412C2.78349 6.3539 2.86743 6.40892 2.95966 6.44598C3.05188 6.48304 3.15056 6.50141 3.24994 6.5H4.31928L5.57806 19.5146C5.7136 20.918 6.90583 22 8.31537 22H15.6835C17.0931 22 18.2854 20.9181 18.4208 19.5146L19.6806 6.5H20.7499C20.8493 6.50141 20.948 6.48304 21.0402 6.44598C21.1324 6.40892 21.2164 6.3539 21.2872 6.28412C21.3579 6.21434 21.4141 6.13118 21.4525 6.03949C21.4909 5.9478 21.5106 5.84939 21.5106 5.75C21.5106 5.65061 21.4909 5.5522 21.4525 5.46051C21.4141 5.36882 21.3579 5.28566 21.2872 5.21588C21.2164 5.1461 21.1324 5.09108 21.0402 5.05402C20.948 5.01696 20.8493 4.99859 20.7499 5H19.122C19.0425 4.98709 18.9613 4.98709 18.8818 5H15.4599C15.2147 3.31072 13.7541 2 11.9999 2ZM11.9999 3.5C12.9395 3.5 13.7103 4.13408 13.9306 5H10.0693C10.2896 4.13408 11.0604 3.5 11.9999 3.5ZM5.82513 6.5H18.1738L16.9277 19.3701C16.8652 20.0177 16.3339 20.5 15.6835 20.5H8.31537C7.66591 20.5 7.13369 20.0168 7.07123 19.3701L5.82513 6.5ZM10.2382 8.98926C10.0395 8.99236 9.8501 9.07423 9.71167 9.21686C9.57324 9.3595 9.49709 9.55125 9.49994 9.75V17.25C9.49853 17.3494 9.5169 17.4481 9.55396 17.5403C9.59102 17.6325 9.64604 17.7164 9.71582 17.7872C9.7856 17.858 9.86876 17.9142 9.96045 17.9526C10.0521 17.9909 10.1505 18.0107 10.2499 18.0107C10.3493 18.0107 10.4477 17.9909 10.5394 17.9526C10.6311 17.9142 10.7143 17.858 10.7841 17.7872C10.8538 17.7164 10.9089 17.6325 10.9459 17.5403C10.983 17.4481 11.0013 17.3494 10.9999 17.25V9.75C11.0014 9.64962 10.9827 9.54997 10.9449 9.45695C10.9071 9.36394 10.851 9.27946 10.78 9.20852C10.709 9.13758 10.6244 9.08161 10.5313 9.04395C10.4383 9.00629 10.3386 8.98769 10.2382 8.98926ZM13.7382 8.98926C13.5395 8.99236 13.3501 9.07423 13.2117 9.21686C13.0732 9.3595 12.9971 9.55125 12.9999 9.75V17.25C12.9985 17.3494 13.0169 17.4481 13.054 17.5403C13.091 17.6325 13.146 17.7164 13.2158 17.7872C13.2856 17.858 13.3688 17.9142 13.4605 17.9526C13.5521 17.9909 13.6505 18.0107 13.7499 18.0107C13.8493 18.0107 13.9477 17.9909 14.0394 17.9526C14.1311 17.9142 14.2143 17.858 14.2841 17.7872C14.3538 17.7164 14.4089 17.6325 14.4459 17.5403C14.483 17.4481 14.5013 17.3494 14.4999 17.25V9.75C14.5014 9.64962 14.4827 9.54997 14.4449 9.45695C14.4071 9.36394 14.351 9.27946 14.28 9.20852C14.209 9.13758 14.1244 9.08161 14.0313 9.04395C13.9383 9.00629 13.8386 8.98769 13.7382 8.98926Z"
              fill={themeColors.neutral890}
            />
          </svg>
        </button>
        <div className="outer-property-wrap pl-20 pr-20 pt-12 pb-12">
          <div className="d-flex mb-0 flex-grow-1 align-items-center">
            <div className="intro-prof-wrap profile-bg-turquoise flex-shrink-0">
              <img className="prof-init-icon" src={ProfInitialsIcon} />
            </div>
            <div className="introduction-content flex-grow-1 ml-10 pe-0 ps-0 pb-0">
              <p className="mb-0 notification-head">
                <span className="f-bold">
                  Your Buyer Listing Want-Listing is Ready to Review & Post!
                </span>{" "}
                {notification?.FirstName} {notification?.LastName} completed
                their search form.{" "}
                <a
                  href={void 0}
                  onClick={() => {
                    getWantAdDetails(notification?.Want_ad_Id);
                  }}
                >
                  Click to review
                </a>
                <span className="time-stamp">
                  {notification?.LastUpdatedTS &&
                    timeAgo(
                      moment.utc(notification?.LastUpdatedTS).toISOString(),
                    )}
                </span>
              </p>
            </div>
          </div>
          {notification?.Read_Status == 1 && (
            <div className="dot-indicator dot-bg-red align-self-center mt-0"></div>
          )}
        </div>
      </div>
    );
  }
  function RenderWantAdSyndicatedNotification({ notification }) {
    return (
      <div className="notification-sec position-relative notify-info-bg pointer">
        <button
          className="btn notif-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            updateInAppDeleteStatusOnClick(
              _.get(notification, "NotificationId", ""),
              notification?.SubjectType,
              true,
              1,
            );
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.9999 2C10.2458 2 8.78514 3.31072 8.53998 5H5.11908C5.0765 4.99271 5.03337 4.98912 4.99017 4.98926C4.95285 4.99006 4.91564 4.99365 4.87885 5H3.24994C3.15056 4.99859 3.05188 5.01696 2.95966 5.05402C2.86743 5.09108 2.78349 5.1461 2.71271 5.21588C2.64194 5.28566 2.58573 5.36882 2.54737 5.46051C2.50901 5.5522 2.48926 5.65061 2.48926 5.75C2.48926 5.84939 2.50901 5.9478 2.54737 6.03949C2.58573 6.13118 2.64194 6.21434 2.71271 6.28412C2.78349 6.3539 2.86743 6.40892 2.95966 6.44598C3.05188 6.48304 3.15056 6.50141 3.24994 6.5H4.31928L5.57806 19.5146C5.7136 20.918 6.90583 22 8.31537 22H15.6835C17.0931 22 18.2854 20.9181 18.4208 19.5146L19.6806 6.5H20.7499C20.8493 6.50141 20.948 6.48304 21.0402 6.44598C21.1324 6.40892 21.2164 6.3539 21.2872 6.28412C21.3579 6.21434 21.4141 6.13118 21.4525 6.03949C21.4909 5.9478 21.5106 5.84939 21.5106 5.75C21.5106 5.65061 21.4909 5.5522 21.4525 5.46051C21.4141 5.36882 21.3579 5.28566 21.2872 5.21588C21.2164 5.1461 21.1324 5.09108 21.0402 5.05402C20.948 5.01696 20.8493 4.99859 20.7499 5H19.122C19.0425 4.98709 18.9613 4.98709 18.8818 5H15.4599C15.2147 3.31072 13.7541 2 11.9999 2ZM11.9999 3.5C12.9395 3.5 13.7103 4.13408 13.9306 5H10.0693C10.2896 4.13408 11.0604 3.5 11.9999 3.5ZM5.82513 6.5H18.1738L16.9277 19.3701C16.8652 20.0177 16.3339 20.5 15.6835 20.5H8.31537C7.66591 20.5 7.13369 20.0168 7.07123 19.3701L5.82513 6.5ZM10.2382 8.98926C10.0395 8.99236 9.8501 9.07423 9.71167 9.21686C9.57324 9.3595 9.49709 9.55125 9.49994 9.75V17.25C9.49853 17.3494 9.5169 17.4481 9.55396 17.5403C9.59102 17.6325 9.64604 17.7164 9.71582 17.7872C9.7856 17.858 9.86876 17.9142 9.96045 17.9526C10.0521 17.9909 10.1505 18.0107 10.2499 18.0107C10.3493 18.0107 10.4477 17.9909 10.5394 17.9526C10.6311 17.9142 10.7143 17.858 10.7841 17.7872C10.8538 17.7164 10.9089 17.6325 10.9459 17.5403C10.983 17.4481 11.0013 17.3494 10.9999 17.25V9.75C11.0014 9.64962 10.9827 9.54997 10.9449 9.45695C10.9071 9.36394 10.851 9.27946 10.78 9.20852C10.709 9.13758 10.6244 9.08161 10.5313 9.04395C10.4383 9.00629 10.3386 8.98769 10.2382 8.98926ZM13.7382 8.98926C13.5395 8.99236 13.3501 9.07423 13.2117 9.21686C13.0732 9.3595 12.9971 9.55125 12.9999 9.75V17.25C12.9985 17.3494 13.0169 17.4481 13.054 17.5403C13.091 17.6325 13.146 17.7164 13.2158 17.7872C13.2856 17.858 13.3688 17.9142 13.4605 17.9526C13.5521 17.9909 13.6505 18.0107 13.7499 18.0107C13.8493 18.0107 13.9477 17.9909 14.0394 17.9526C14.1311 17.9142 14.2143 17.858 14.2841 17.7872C14.3538 17.7164 14.4089 17.6325 14.4459 17.5403C14.483 17.4481 14.5013 17.3494 14.4999 17.25V9.75C14.5014 9.64962 14.4827 9.54997 14.4449 9.45695C14.4071 9.36394 14.351 9.27946 14.28 9.20852C14.209 9.13758 14.1244 9.08161 14.0313 9.04395C13.9383 9.00629 13.8386 8.98769 13.7382 8.98926Z"
              fill={themeColors.neutral890}
            />
          </svg>
        </button>
        <div className="outer-property-wrap pl-20 pr-20 pt-12 pb-12">
          <div className="d-flex mb-0 flex-grow-1 align-items-center">
            <div className="intro-prof-wrap profile-bg-turquoise flex-shrink-0">
              <img className="prof-init-icon" src={HomeIcon} />
            </div>
            <div className="introduction-content flex-grow-1 ml-10 pe-0 ps-0 pb-0">
              <p className="mb-0 notification-head">
                <span className="f-bold">
                  &ldquo;{notification?.wantAdTitle}&rdquo; is now syndicated to
                  the public through Gitcha.
                </span>{" "}
                <a
                  href={void 0}
                  onClick={() => {
                    getWantAdDetails(notification?.Want_ad_Id);
                  }}
                >
                  View Want-Listing
                </a>
                <span className="time-stamp">
                  {notification?.LastUpdatedTS &&
                    timeAgo(
                      moment.utc(notification?.LastUpdatedTS).toISOString(),
                    )}
                </span>
              </p>
            </div>
          </div>
          {notification?.Read_Status == 1 && (
            <div className="dot-indicator dot-bg-red align-self-center mt-0"></div>
          )}
        </div>
      </div>
    );
  }
  /** Notification for plan about to expire */

  function RenderPlanExpireNotification({ notification }) {
    return (
      <div
        className="notification-sec position-relative notify-info-bg pointer"
        role="button"
        onClick={(e) => {
          e.stopPropagation();
          navigate(pageURLs.settings, { state: { isFromNotification: true } });
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          navigate(pageURLs.settings, { state: { isFromNotification: true } });
        }}
      >
        <button
          className="btn notif-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            updateInAppDeleteStatusOnClick(
              _.get(notification, "NotificationId", ""),
              notification?.SubjectType,
              true,
              1,
            );
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.9999 2C10.2458 2 8.78514 3.31072 8.53998 5H5.11908C5.0765 4.99271 5.03337 4.98912 4.99017 4.98926C4.95285 4.99006 4.91564 4.99365 4.87885 5H3.24994C3.15056 4.99859 3.05188 5.01696 2.95966 5.05402C2.86743 5.09108 2.78349 5.1461 2.71271 5.21588C2.64194 5.28566 2.58573 5.36882 2.54737 5.46051C2.50901 5.5522 2.48926 5.65061 2.48926 5.75C2.48926 5.84939 2.50901 5.9478 2.54737 6.03949C2.58573 6.13118 2.64194 6.21434 2.71271 6.28412C2.78349 6.3539 2.86743 6.40892 2.95966 6.44598C3.05188 6.48304 3.15056 6.50141 3.24994 6.5H4.31928L5.57806 19.5146C5.7136 20.918 6.90583 22 8.31537 22H15.6835C17.0931 22 18.2854 20.9181 18.4208 19.5146L19.6806 6.5H20.7499C20.8493 6.50141 20.948 6.48304 21.0402 6.44598C21.1324 6.40892 21.2164 6.3539 21.2872 6.28412C21.3579 6.21434 21.4141 6.13118 21.4525 6.03949C21.4909 5.9478 21.5106 5.84939 21.5106 5.75C21.5106 5.65061 21.4909 5.5522 21.4525 5.46051C21.4141 5.36882 21.3579 5.28566 21.2872 5.21588C21.2164 5.1461 21.1324 5.09108 21.0402 5.05402C20.948 5.01696 20.8493 4.99859 20.7499 5H19.122C19.0425 4.98709 18.9613 4.98709 18.8818 5H15.4599C15.2147 3.31072 13.7541 2 11.9999 2ZM11.9999 3.5C12.9395 3.5 13.7103 4.13408 13.9306 5H10.0693C10.2896 4.13408 11.0604 3.5 11.9999 3.5ZM5.82513 6.5H18.1738L16.9277 19.3701C16.8652 20.0177 16.3339 20.5 15.6835 20.5H8.31537C7.66591 20.5 7.13369 20.0168 7.07123 19.3701L5.82513 6.5ZM10.2382 8.98926C10.0395 8.99236 9.8501 9.07423 9.71167 9.21686C9.57324 9.3595 9.49709 9.55125 9.49994 9.75V17.25C9.49853 17.3494 9.5169 17.4481 9.55396 17.5403C9.59102 17.6325 9.64604 17.7164 9.71582 17.7872C9.7856 17.858 9.86876 17.9142 9.96045 17.9526C10.0521 17.9909 10.1505 18.0107 10.2499 18.0107C10.3493 18.0107 10.4477 17.9909 10.5394 17.9526C10.6311 17.9142 10.7143 17.858 10.7841 17.7872C10.8538 17.7164 10.9089 17.6325 10.9459 17.5403C10.983 17.4481 11.0013 17.3494 10.9999 17.25V9.75C11.0014 9.64962 10.9827 9.54997 10.9449 9.45695C10.9071 9.36394 10.851 9.27946 10.78 9.20852C10.709 9.13758 10.6244 9.08161 10.5313 9.04395C10.4383 9.00629 10.3386 8.98769 10.2382 8.98926ZM13.7382 8.98926C13.5395 8.99236 13.3501 9.07423 13.2117 9.21686C13.0732 9.3595 12.9971 9.55125 12.9999 9.75V17.25C12.9985 17.3494 13.0169 17.4481 13.054 17.5403C13.091 17.6325 13.146 17.7164 13.2158 17.7872C13.2856 17.858 13.3688 17.9142 13.4605 17.9526C13.5521 17.9909 13.6505 18.0107 13.7499 18.0107C13.8493 18.0107 13.9477 17.9909 14.0394 17.9526C14.1311 17.9142 14.2143 17.858 14.2841 17.7872C14.3538 17.7164 14.4089 17.6325 14.4459 17.5403C14.483 17.4481 14.5013 17.3494 14.4999 17.25V9.75C14.5014 9.64962 14.4827 9.54997 14.4449 9.45695C14.4071 9.36394 14.351 9.27946 14.28 9.20852C14.209 9.13758 14.1244 9.08161 14.0313 9.04395C13.9383 9.00629 13.8386 8.98769 13.7382 8.98926Z"
              fill={themeColors.neutral890}
            />
          </svg>
        </button>
        <div className="outer-property-wrap pl-20 pr-20 pt-12 pb-12">
          <div className="d-flex mb-0 flex-grow-1 align-items-center">
            <div className="intro-prof-wrap profile-bg-darkmeroon flex-shrink-0">
              <img className="prof-init-icon" src={NotifClockIcon} />
            </div>
            <div className="introduction-content flex-grow-1 ml-10 pe-0 ps-0 pb-0">
              <p className="mb-0 notification-head">
                <span className="f-bold">
                  {" "}
                  Your {notification?.PlanName} Membership Expires Soon{" "}
                </span>{" "}
                on {formateDate(notification?.PlanToDate, "mm/dd/yyyy")}. Renew
                to keep all of your client info, inventory, and agent dashboard
                features before it expires!
                <span className="time-stamp">
                  {notification?.LastUpdatedTS &&
                    timeAgo(
                      moment.utc(notification?.LastUpdatedTS).toISOString(),
                    )}
                </span>
              </p>
            </div>
          </div>
          {notification?.Read_Status == 1 && (
            <div className="dot-indicator dot-bg-red align-self-center mt-0"></div>
          )}
        </div>
      </div>
    );
  }
  /** Notification for  card declined */

  function RenderCardDeclinedNotification({ notification }) {
    return (
      <div
        className="notification-sec position-relative notify-info-bg pointer"
        role="button"
        onClick={(e) => {
          e.stopPropagation();
          navigate(pageURLs.addNewCard);
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          navigate(pageURLs.addNewCard);
        }}
      >
        <button
          className="btn notif-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            updateInAppDeleteStatusOnClick(
              _.get(notification, "NotificationId", ""),
              notification?.SubjectType,
              true,
              1,
            );
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.9999 2C10.2458 2 8.78514 3.31072 8.53998 5H5.11908C5.0765 4.99271 5.03337 4.98912 4.99017 4.98926C4.95285 4.99006 4.91564 4.99365 4.87885 5H3.24994C3.15056 4.99859 3.05188 5.01696 2.95966 5.05402C2.86743 5.09108 2.78349 5.1461 2.71271 5.21588C2.64194 5.28566 2.58573 5.36882 2.54737 5.46051C2.50901 5.5522 2.48926 5.65061 2.48926 5.75C2.48926 5.84939 2.50901 5.9478 2.54737 6.03949C2.58573 6.13118 2.64194 6.21434 2.71271 6.28412C2.78349 6.3539 2.86743 6.40892 2.95966 6.44598C3.05188 6.48304 3.15056 6.50141 3.24994 6.5H4.31928L5.57806 19.5146C5.7136 20.918 6.90583 22 8.31537 22H15.6835C17.0931 22 18.2854 20.9181 18.4208 19.5146L19.6806 6.5H20.7499C20.8493 6.50141 20.948 6.48304 21.0402 6.44598C21.1324 6.40892 21.2164 6.3539 21.2872 6.28412C21.3579 6.21434 21.4141 6.13118 21.4525 6.03949C21.4909 5.9478 21.5106 5.84939 21.5106 5.75C21.5106 5.65061 21.4909 5.5522 21.4525 5.46051C21.4141 5.36882 21.3579 5.28566 21.2872 5.21588C21.2164 5.1461 21.1324 5.09108 21.0402 5.05402C20.948 5.01696 20.8493 4.99859 20.7499 5H19.122C19.0425 4.98709 18.9613 4.98709 18.8818 5H15.4599C15.2147 3.31072 13.7541 2 11.9999 2ZM11.9999 3.5C12.9395 3.5 13.7103 4.13408 13.9306 5H10.0693C10.2896 4.13408 11.0604 3.5 11.9999 3.5ZM5.82513 6.5H18.1738L16.9277 19.3701C16.8652 20.0177 16.3339 20.5 15.6835 20.5H8.31537C7.66591 20.5 7.13369 20.0168 7.07123 19.3701L5.82513 6.5ZM10.2382 8.98926C10.0395 8.99236 9.8501 9.07423 9.71167 9.21686C9.57324 9.3595 9.49709 9.55125 9.49994 9.75V17.25C9.49853 17.3494 9.5169 17.4481 9.55396 17.5403C9.59102 17.6325 9.64604 17.7164 9.71582 17.7872C9.7856 17.858 9.86876 17.9142 9.96045 17.9526C10.0521 17.9909 10.1505 18.0107 10.2499 18.0107C10.3493 18.0107 10.4477 17.9909 10.5394 17.9526C10.6311 17.9142 10.7143 17.858 10.7841 17.7872C10.8538 17.7164 10.9089 17.6325 10.9459 17.5403C10.983 17.4481 11.0013 17.3494 10.9999 17.25V9.75C11.0014 9.64962 10.9827 9.54997 10.9449 9.45695C10.9071 9.36394 10.851 9.27946 10.78 9.20852C10.709 9.13758 10.6244 9.08161 10.5313 9.04395C10.4383 9.00629 10.3386 8.98769 10.2382 8.98926ZM13.7382 8.98926C13.5395 8.99236 13.3501 9.07423 13.2117 9.21686C13.0732 9.3595 12.9971 9.55125 12.9999 9.75V17.25C12.9985 17.3494 13.0169 17.4481 13.054 17.5403C13.091 17.6325 13.146 17.7164 13.2158 17.7872C13.2856 17.858 13.3688 17.9142 13.4605 17.9526C13.5521 17.9909 13.6505 18.0107 13.7499 18.0107C13.8493 18.0107 13.9477 17.9909 14.0394 17.9526C14.1311 17.9142 14.2143 17.858 14.2841 17.7872C14.3538 17.7164 14.4089 17.6325 14.4459 17.5403C14.483 17.4481 14.5013 17.3494 14.4999 17.25V9.75C14.5014 9.64962 14.4827 9.54997 14.4449 9.45695C14.4071 9.36394 14.351 9.27946 14.28 9.20852C14.209 9.13758 14.1244 9.08161 14.0313 9.04395C13.9383 9.00629 13.8386 8.98769 13.7382 8.98926Z"
              fill={themeColors.neutral890}
            />
          </svg>
        </button>
        <div className="outer-property-wrap pl-20 pr-20 pt-12 pb-12">
          <div className="d-flex mb-0 flex-grow-1 align-items-center">
            <div className="intro-prof-wrap profile-bg-darkmeroon flex-shrink-0">
              <img className="prof-init-icon" src={NotifClockIcon} />
            </div>
            <div className="introduction-content flex-grow-1 ml-10 pe-0 ps-0 pb-0">
              <p className="mb-0 notification-head">
                <span className="f-bold">Oh No! Your Card Was Declined.</span>{" "}
                Change your payment method so you can get back in the action
                without losing all of your progress. Your account data will be
                stored for 180 days until it’s deleted for good.
                <span className="time-stamp">
                  {notification?.LastUpdatedTS &&
                    timeAgo(
                      moment.utc(notification?.LastUpdatedTS).toISOString(),
                    )}
                </span>
              </p>
            </div>
          </div>
          {notification?.Read_Status == 1 && (
            <div className="dot-indicator dot-bg-red align-self-center mt-0"></div>
          )}
        </div>
      </div>
    );
  }
  /** Notification for  plan expired */

  function RenderPlanExpiredNotification({ notification }) {
    return (
      <div
        className="notification-sec position-relative notify-info-bg pointer"
        role="button"
        onClick={(e) => {
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
      >
        <button
          className="btn notif-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            updateInAppDeleteStatusOnClick(
              _.get(notification, "NotificationId", ""),
              notification?.SubjectType,
              true,
              1,
            );
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.9999 2C10.2458 2 8.78514 3.31072 8.53998 5H5.11908C5.0765 4.99271 5.03337 4.98912 4.99017 4.98926C4.95285 4.99006 4.91564 4.99365 4.87885 5H3.24994C3.15056 4.99859 3.05188 5.01696 2.95966 5.05402C2.86743 5.09108 2.78349 5.1461 2.71271 5.21588C2.64194 5.28566 2.58573 5.36882 2.54737 5.46051C2.50901 5.5522 2.48926 5.65061 2.48926 5.75C2.48926 5.84939 2.50901 5.9478 2.54737 6.03949C2.58573 6.13118 2.64194 6.21434 2.71271 6.28412C2.78349 6.3539 2.86743 6.40892 2.95966 6.44598C3.05188 6.48304 3.15056 6.50141 3.24994 6.5H4.31928L5.57806 19.5146C5.7136 20.918 6.90583 22 8.31537 22H15.6835C17.0931 22 18.2854 20.9181 18.4208 19.5146L19.6806 6.5H20.7499C20.8493 6.50141 20.948 6.48304 21.0402 6.44598C21.1324 6.40892 21.2164 6.3539 21.2872 6.28412C21.3579 6.21434 21.4141 6.13118 21.4525 6.03949C21.4909 5.9478 21.5106 5.84939 21.5106 5.75C21.5106 5.65061 21.4909 5.5522 21.4525 5.46051C21.4141 5.36882 21.3579 5.28566 21.2872 5.21588C21.2164 5.1461 21.1324 5.09108 21.0402 5.05402C20.948 5.01696 20.8493 4.99859 20.7499 5H19.122C19.0425 4.98709 18.9613 4.98709 18.8818 5H15.4599C15.2147 3.31072 13.7541 2 11.9999 2ZM11.9999 3.5C12.9395 3.5 13.7103 4.13408 13.9306 5H10.0693C10.2896 4.13408 11.0604 3.5 11.9999 3.5ZM5.82513 6.5H18.1738L16.9277 19.3701C16.8652 20.0177 16.3339 20.5 15.6835 20.5H8.31537C7.66591 20.5 7.13369 20.0168 7.07123 19.3701L5.82513 6.5ZM10.2382 8.98926C10.0395 8.99236 9.8501 9.07423 9.71167 9.21686C9.57324 9.3595 9.49709 9.55125 9.49994 9.75V17.25C9.49853 17.3494 9.5169 17.4481 9.55396 17.5403C9.59102 17.6325 9.64604 17.7164 9.71582 17.7872C9.7856 17.858 9.86876 17.9142 9.96045 17.9526C10.0521 17.9909 10.1505 18.0107 10.2499 18.0107C10.3493 18.0107 10.4477 17.9909 10.5394 17.9526C10.6311 17.9142 10.7143 17.858 10.7841 17.7872C10.8538 17.7164 10.9089 17.6325 10.9459 17.5403C10.983 17.4481 11.0013 17.3494 10.9999 17.25V9.75C11.0014 9.64962 10.9827 9.54997 10.9449 9.45695C10.9071 9.36394 10.851 9.27946 10.78 9.20852C10.709 9.13758 10.6244 9.08161 10.5313 9.04395C10.4383 9.00629 10.3386 8.98769 10.2382 8.98926ZM13.7382 8.98926C13.5395 8.99236 13.3501 9.07423 13.2117 9.21686C13.0732 9.3595 12.9971 9.55125 12.9999 9.75V17.25C12.9985 17.3494 13.0169 17.4481 13.054 17.5403C13.091 17.6325 13.146 17.7164 13.2158 17.7872C13.2856 17.858 13.3688 17.9142 13.4605 17.9526C13.5521 17.9909 13.6505 18.0107 13.7499 18.0107C13.8493 18.0107 13.9477 17.9909 14.0394 17.9526C14.1311 17.9142 14.2143 17.858 14.2841 17.7872C14.3538 17.7164 14.4089 17.6325 14.4459 17.5403C14.483 17.4481 14.5013 17.3494 14.4999 17.25V9.75C14.5014 9.64962 14.4827 9.54997 14.4449 9.45695C14.4071 9.36394 14.351 9.27946 14.28 9.20852C14.209 9.13758 14.1244 9.08161 14.0313 9.04395C13.9383 9.00629 13.8386 8.98769 13.7382 8.98926Z"
              fill={themeColors.neutral890}
            />
          </svg>
        </button>
        <div className="outer-property-wrap pl-20 pr-20 pt-12 pb-12">
          <div className="d-flex mb-0 flex-grow-1 align-items-center">
            <div className="intro-prof-wrap profile-bg-darkmeroon flex-shrink-0">
              <img className="prof-init-icon" src={NotifClockIcon} />
            </div>
            <div className="introduction-content flex-grow-1 ml-10 pe-0 ps-0 pb-0">
              <p className="mb-0 notification-head">
                <span className="f-bold">
                  Your {notification.PlanName} Membership Expired
                </span>{" "}
                on {formateDate(notification?.PlanToDate, "mm/dd/yyyy")}. You’ve
                been downgraded to the lite version of Gitcha. All of your
                member data will be stored for 180 days until it’s deleted for
                good.
                <span className="time-stamp">
                  {notification?.LastUpdatedTS &&
                    timeAgo(
                      moment.utc(notification?.LastUpdatedTS).toISOString(),
                    )}
                </span>
              </p>
            </div>
          </div>
          {notification?.Read_Status == 1 && (
            <div className="dot-indicator dot-bg-red align-self-center mt-0"></div>
          )}
        </div>
      </div>
    );
  }

  /** Notification for admin messages */
  function RenderAdminNotification({ notification }) {
    return (
      <div
        className="notification-sec position-relative notify-info-bg pointer"
        role="button"
        onClick={(e) => {
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
      >
        <button
          className="btn notif-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            updateInAppDeleteStatusOnClick(
              _.get(notification, "NotificationId", ""),
              notification?.SubjectType,
              true,
              1,
            );
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.9999 2C10.2458 2 8.78514 3.31072 8.53998 5H5.11908C5.0765 4.99271 5.03337 4.98912 4.99017 4.98926C4.95285 4.99006 4.91564 4.99365 4.87885 5H3.24994C3.15056 4.99859 3.05188 5.01696 2.95966 5.05402C2.86743 5.09108 2.78349 5.1461 2.71271 5.21588C2.64194 5.28566 2.58573 5.36882 2.54737 5.46051C2.50901 5.5522 2.48926 5.65061 2.48926 5.75C2.48926 5.84939 2.50901 5.9478 2.54737 6.03949C2.58573 6.13118 2.64194 6.21434 2.71271 6.28412C2.78349 6.3539 2.86743 6.40892 2.95966 6.44598C3.05188 6.48304 3.15056 6.50141 3.24994 6.5H4.31928L5.57806 19.5146C5.7136 20.918 6.90583 22 8.31537 22H15.6835C17.0931 22 18.2854 20.9181 18.4208 19.5146L19.6806 6.5H20.7499C20.8493 6.50141 20.948 6.48304 21.0402 6.44598C21.1324 6.40892 21.2164 6.3539 21.2872 6.28412C21.3579 6.21434 21.4141 6.13118 21.4525 6.03949C21.4909 5.9478 21.5106 5.84939 21.5106 5.75C21.5106 5.65061 21.4909 5.5522 21.4525 5.46051C21.4141 5.36882 21.3579 5.28566 21.2872 5.21588C21.2164 5.1461 21.1324 5.09108 21.0402 5.05402C20.948 5.01696 20.8493 4.99859 20.7499 5H19.122C19.0425 4.98709 18.9613 4.98709 18.8818 5H15.4599C15.2147 3.31072 13.7541 2 11.9999 2ZM11.9999 3.5C12.9395 3.5 13.7103 4.13408 13.9306 5H10.0693C10.2896 4.13408 11.0604 3.5 11.9999 3.5ZM5.82513 6.5H18.1738L16.9277 19.3701C16.8652 20.0177 16.3339 20.5 15.6835 20.5H8.31537C7.66591 20.5 7.13369 20.0168 7.07123 19.3701L5.82513 6.5ZM10.2382 8.98926C10.0395 8.99236 9.8501 9.07423 9.71167 9.21686C9.57324 9.3595 9.49709 9.55125 9.49994 9.75V17.25C9.49853 17.3494 9.5169 17.4481 9.55396 17.5403C9.59102 17.6325 9.64604 17.7164 9.71582 17.7872C9.7856 17.858 9.86876 17.9142 9.96045 17.9526C10.0521 17.9909 10.1505 18.0107 10.2499 18.0107C10.3493 18.0107 10.4477 17.9909 10.5394 17.9526C10.6311 17.9142 10.7143 17.858 10.7841 17.7872C10.8538 17.7164 10.9089 17.6325 10.9459 17.5403C10.983 17.4481 11.0013 17.3494 10.9999 17.25V9.75C11.0014 9.64962 10.9827 9.54997 10.9449 9.45695C10.9071 9.36394 10.851 9.27946 10.78 9.20852C10.709 9.13758 10.6244 9.08161 10.5313 9.04395C10.4383 9.00629 10.3386 8.98769 10.2382 8.98926ZM13.7382 8.98926C13.5395 8.99236 13.3501 9.07423 13.2117 9.21686C13.0732 9.3595 12.9971 9.55125 12.9999 9.75V17.25C12.9985 17.3494 13.0169 17.4481 13.054 17.5403C13.091 17.6325 13.146 17.7164 13.2158 17.7872C13.2856 17.858 13.3688 17.9142 13.4605 17.9526C13.5521 17.9909 13.6505 18.0107 13.7499 18.0107C13.8493 18.0107 13.9477 17.9909 14.0394 17.9526C14.1311 17.9142 14.2143 17.858 14.2841 17.7872C14.3538 17.7164 14.4089 17.6325 14.4459 17.5403C14.483 17.4481 14.5013 17.3494 14.4999 17.25V9.75C14.5014 9.64962 14.4827 9.54997 14.4449 9.45695C14.4071 9.36394 14.351 9.27946 14.28 9.20852C14.209 9.13758 14.1244 9.08161 14.0313 9.04395C13.9383 9.00629 13.8386 8.98769 13.7382 8.98926Z"
              fill={themeColors.neutral890}
            />
          </svg>
        </button>
        <div className="outer-property-wrap pl-20 pr-20 pt-12 pb-12">
          <div className="d-flex mb-0 flex-grow-1 align-items-center">
            <div className="intro-prof-wrap profile-bg-skyblue-variant1 flex-shrink-0">
              <img className="prof-init-icon" src={MarketingIcon} />
            </div>
            <div className="introduction-content flex-grow-1 ml-10 pe-0 ps-0 pb-0">
              <p className="mb-0 notification-head">
                {/* <span className="f-bold">Message from Admin:</span>{" "} */}
                {notification?.NotificationText}
                <span className="time-stamp">
                  {notification?.LastUpdatedTS &&
                    timeAgo(
                      moment.utc(notification?.LastUpdatedTS).toISOString(),
                    )}
                </span>
              </p>
            </div>
          </div>
          {notification?.Read_Status == 1 && (
            <div className="dot-indicator dot-bg-red align-self-center mt-0"></div>
          )}
        </div>
      </div>
    );
  }

  function RenderUpgradeNudgeNotification({ notification }) {
    return (
      <div className="notification-sec position-relative notify-info-bg pointer">
        <button
          className="btn notif-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            updateInAppDeleteStatusOnClick(
              _.get(notification, "NotificationId", ""),
              notification?.SubjectType,
              true,
              1,
            );
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.9999 2C10.2458 2 8.78514 3.31072 8.53998 5H5.11908C5.0765 4.99271 5.03337 4.98912 4.99017 4.98926C4.95285 4.99006 4.91564 4.99365 4.87885 5H3.24994C3.15056 4.99859 3.05188 5.01696 2.95966 5.05402C2.86743 5.09108 2.78349 5.1461 2.71271 5.21588C2.64194 5.28566 2.58573 5.36882 2.54737 5.46051C2.50901 5.5522 2.48926 5.65061 2.48926 5.75C2.48926 5.84939 2.50901 5.9478 2.54737 6.03949C2.58573 6.13118 2.64194 6.21434 2.71271 6.28412C2.78349 6.3539 2.86743 6.40892 2.95966 6.44598C3.05188 6.48304 3.15056 6.50141 3.24994 6.5H4.31928L5.57806 19.5146C5.7136 20.918 6.90583 22 8.31537 22H15.6835C17.0931 22 18.2854 20.9181 18.4208 19.5146L19.6806 6.5H20.7499C20.8493 6.50141 20.948 6.48304 21.0402 6.44598C21.1324 6.40892 21.2164 6.3539 21.2872 6.28412C21.3579 6.21434 21.4141 6.13118 21.4525 6.03949C21.4909 5.9478 21.5106 5.84939 21.5106 5.75C21.5106 5.65061 21.4909 5.5522 21.4525 5.46051C21.4141 5.36882 21.3579 5.28566 21.2872 5.21588C21.2164 5.1461 21.1324 5.09108 21.0402 5.05402C20.948 5.01696 20.8493 4.99859 20.7499 5H19.122C19.0425 4.98709 18.9613 4.98709 18.8818 5H15.4599C15.2147 3.31072 13.7541 2 11.9999 2ZM11.9999 3.5C12.9395 3.5 13.7103 4.13408 13.9306 5H10.0693C10.2896 4.13408 11.0604 3.5 11.9999 3.5ZM5.82513 6.5H18.1738L16.9277 19.3701C16.8652 20.0177 16.3339 20.5 15.6835 20.5H8.31537C7.66591 20.5 7.13369 20.0168 7.07123 19.3701L5.82513 6.5ZM10.2382 8.98926C10.0395 8.99236 9.8501 9.07423 9.71167 9.21686C9.57324 9.3595 9.49709 9.55125 9.49994 9.75V17.25C9.49853 17.3494 9.5169 17.4481 9.55396 17.5403C9.59102 17.6325 9.64604 17.7164 9.71582 17.7872C9.7856 17.858 9.86876 17.9142 9.96045 17.9526C10.0521 17.9909 10.1505 18.0107 10.2499 18.0107C10.3493 18.0107 10.4477 17.9909 10.5394 17.9526C10.6311 17.9142 10.7143 17.858 10.7841 17.7872C10.8538 17.7164 10.9089 17.6325 10.9459 17.5403C10.983 17.4481 11.0013 17.3494 10.9999 17.25V9.75C11.0014 9.64962 10.9827 9.54997 10.9449 9.45695C10.9071 9.36394 10.851 9.27946 10.78 9.20852C10.709 9.13758 10.6244 9.08161 10.5313 9.04395C10.4383 9.00629 10.3386 8.98769 10.2382 8.98926ZM13.7382 8.98926C13.5395 8.99236 13.3501 9.07423 13.2117 9.21686C13.0732 9.3595 12.9971 9.55125 12.9999 9.75V17.25C12.9985 17.3494 13.0169 17.4481 13.054 17.5403C13.091 17.6325 13.146 17.7164 13.2158 17.7872C13.2856 17.858 13.3688 17.9142 13.4605 17.9526C13.5521 17.9909 13.6505 18.0107 13.7499 18.0107C13.8493 18.0107 13.9477 17.9909 14.0394 17.9526C14.1311 17.9142 14.2143 17.858 14.2841 17.7872C14.3538 17.7164 14.4089 17.6325 14.4459 17.5403C14.483 17.4481 14.5013 17.3494 14.4999 17.25V9.75C14.5014 9.64962 14.4827 9.54997 14.4449 9.45695C14.4071 9.36394 14.351 9.27946 14.28 9.20852C14.209 9.13758 14.1244 9.08161 14.0313 9.04395C13.9383 9.00629 13.8386 8.98769 13.7382 8.98926Z"
              fill={themeColors.neutral890}
            />
          </svg>
        </button>
        <div className="outer-property-wrap pl-20 pr-20 pt-12 pb-12">
          <div className="d-flex mb-0 flex-grow-1 align-items-center">
            <div className="intro-prof-wrap profile-bg-skyblue-variant1 flex-shrink-0">
              <img className="prof-init-icon" src={MarketingIcon} />
            </div>
            <div className="introduction-content flex-grow-1 ml-10 pe-0 ps-0 pb-0">
              <p className="mb-0 notification-head">
                <span className="f-bold">
                  Unlock more with an upgrade.
                </span>{" "}
                <a
                  href={void 0}
                  onClick={() => {
                    navigate(pageURLs.selectYourPlan);
                  }}
                >
                  See Plans
                </a>
                <span className="time-stamp">
                  {notification?.LastUpdatedTS &&
                    timeAgo(
                      moment.utc(notification?.LastUpdatedTS).toISOString(),
                    )}
                </span>
              </p>
            </div>
          </div>
          {notification?.Read_Status == 1 && (
            <div className="dot-indicator dot-bg-red align-self-center mt-0"></div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="top-links-header mb-1">
        <h6>Notifications</h6>
        <div className="side-nav-wrap">
          {isLoadNotificationList && isListItems ? (
            <button
              className="btn dpdown-trashbtn"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteInappNotification();
              }}
              disabled={isLoadNotificationList && !isListItems}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.9999 2C10.2458 2 8.78514 3.31072 8.53998 5H5.11908C5.0765 4.99271 5.03337 4.98912 4.99017 4.98926C4.95285 4.99006 4.91564 4.99365 4.87885 5H3.24994C3.15056 4.99859 3.05188 5.01696 2.95966 5.05402C2.86743 5.09108 2.78349 5.1461 2.71271 5.21588C2.64194 5.28566 2.58573 5.36882 2.54737 5.46051C2.50901 5.5522 2.48926 5.65061 2.48926 5.75C2.48926 5.84939 2.50901 5.9478 2.54737 6.03949C2.58573 6.13118 2.64194 6.21434 2.71271 6.28412C2.78349 6.3539 2.86743 6.40892 2.95966 6.44598C3.05188 6.48304 3.15056 6.50141 3.24994 6.5H4.31928L5.57806 19.5146C5.7136 20.918 6.90583 22 8.31537 22H15.6835C17.0931 22 18.2854 20.9181 18.4208 19.5146L19.6806 6.5H20.7499C20.8493 6.50141 20.948 6.48304 21.0402 6.44598C21.1324 6.40892 21.2164 6.3539 21.2872 6.28412C21.3579 6.21434 21.4141 6.13118 21.4525 6.03949C21.4909 5.9478 21.5106 5.84939 21.5106 5.75C21.5106 5.65061 21.4909 5.5522 21.4525 5.46051C21.4141 5.36882 21.3579 5.28566 21.2872 5.21588C21.2164 5.1461 21.1324 5.09108 21.0402 5.05402C20.948 5.01696 20.8493 4.99859 20.7499 5H19.122C19.0425 4.98709 18.9613 4.98709 18.8818 5H15.4599C15.2147 3.31072 13.7541 2 11.9999 2ZM11.9999 3.5C12.9395 3.5 13.7103 4.13408 13.9306 5H10.0693C10.2896 4.13408 11.0604 3.5 11.9999 3.5ZM5.82513 6.5H18.1738L16.9277 19.3701C16.8652 20.0177 16.3339 20.5 15.6835 20.5H8.31537C7.66591 20.5 7.13369 20.0168 7.07123 19.3701L5.82513 6.5ZM10.2382 8.98926C10.0395 8.99236 9.8501 9.07423 9.71167 9.21686C9.57324 9.3595 9.49709 9.55125 9.49994 9.75V17.25C9.49853 17.3494 9.5169 17.4481 9.55396 17.5403C9.59102 17.6325 9.64604 17.7164 9.71582 17.7872C9.7856 17.858 9.86876 17.9142 9.96045 17.9526C10.0521 17.9909 10.1505 18.0107 10.2499 18.0107C10.3493 18.0107 10.4477 17.9909 10.5394 17.9526C10.6311 17.9142 10.7143 17.858 10.7841 17.7872C10.8538 17.7164 10.9089 17.6325 10.9459 17.5403C10.983 17.4481 11.0013 17.3494 10.9999 17.25V9.75C11.0014 9.64962 10.9827 9.54997 10.9449 9.45695C10.9071 9.36394 10.851 9.27946 10.78 9.20852C10.709 9.13758 10.6244 9.08161 10.5313 9.04395C10.4383 9.00629 10.3386 8.98769 10.2382 8.98926ZM13.7382 8.98926C13.5395 8.99236 13.3501 9.07423 13.2117 9.21686C13.0732 9.3595 12.9971 9.55125 12.9999 9.75V17.25C12.9985 17.3494 13.0169 17.4481 13.054 17.5403C13.091 17.6325 13.146 17.7164 13.2158 17.7872C13.2856 17.858 13.3688 17.9142 13.4605 17.9526C13.5521 17.9909 13.6505 18.0107 13.7499 18.0107C13.8493 18.0107 13.9477 17.9909 14.0394 17.9526C14.1311 17.9142 14.2143 17.858 14.2841 17.7872C14.3538 17.7164 14.4089 17.6325 14.4459 17.5403C14.483 17.4481 14.5013 17.3494 14.4999 17.25V9.75C14.5014 9.64962 14.4827 9.54997 14.4449 9.45695C14.4071 9.36394 14.351 9.27946 14.28 9.20852C14.209 9.13758 14.1244 9.08161 14.0313 9.04395C13.9383 9.00629 13.8386 8.98769 13.7382 8.98926Z"
                  fill={themeColors.neutral890}
                />
              </svg>
            </button>
          ) : (
            <button
              className="btn dpdown-trashbtn"
              onClick={(e) => {
                e.preventDefault();
              }}
              disabled={isLoadNotificationList && !isListItems}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.9999 2C10.2458 2 8.78514 3.31072 8.53998 5H5.11908C5.0765 4.99271 5.03337 4.98912 4.99017 4.98926C4.95285 4.99006 4.91564 4.99365 4.87885 5H3.24994C3.15056 4.99859 3.05188 5.01696 2.95966 5.05402C2.86743 5.09108 2.78349 5.1461 2.71271 5.21588C2.64194 5.28566 2.58573 5.36882 2.54737 5.46051C2.50901 5.5522 2.48926 5.65061 2.48926 5.75C2.48926 5.84939 2.50901 5.9478 2.54737 6.03949C2.58573 6.13118 2.64194 6.21434 2.71271 6.28412C2.78349 6.3539 2.86743 6.40892 2.95966 6.44598C3.05188 6.48304 3.15056 6.50141 3.24994 6.5H4.31928L5.57806 19.5146C5.7136 20.918 6.90583 22 8.31537 22H15.6835C17.0931 22 18.2854 20.9181 18.4208 19.5146L19.6806 6.5H20.7499C20.8493 6.50141 20.948 6.48304 21.0402 6.44598C21.1324 6.40892 21.2164 6.3539 21.2872 6.28412C21.3579 6.21434 21.4141 6.13118 21.4525 6.03949C21.4909 5.9478 21.5106 5.84939 21.5106 5.75C21.5106 5.65061 21.4909 5.5522 21.4525 5.46051C21.4141 5.36882 21.3579 5.28566 21.2872 5.21588C21.2164 5.1461 21.1324 5.09108 21.0402 5.05402C20.948 5.01696 20.8493 4.99859 20.7499 5H19.122C19.0425 4.98709 18.9613 4.98709 18.8818 5H15.4599C15.2147 3.31072 13.7541 2 11.9999 2ZM11.9999 3.5C12.9395 3.5 13.7103 4.13408 13.9306 5H10.0693C10.2896 4.13408 11.0604 3.5 11.9999 3.5ZM5.82513 6.5H18.1738L16.9277 19.3701C16.8652 20.0177 16.3339 20.5 15.6835 20.5H8.31537C7.66591 20.5 7.13369 20.0168 7.07123 19.3701L5.82513 6.5ZM10.2382 8.98926C10.0395 8.99236 9.8501 9.07423 9.71167 9.21686C9.57324 9.3595 9.49709 9.55125 9.49994 9.75V17.25C9.49853 17.3494 9.5169 17.4481 9.55396 17.5403C9.59102 17.6325 9.64604 17.7164 9.71582 17.7872C9.7856 17.858 9.86876 17.9142 9.96045 17.9526C10.0521 17.9909 10.1505 18.0107 10.2499 18.0107C10.3493 18.0107 10.4477 17.9909 10.5394 17.9526C10.6311 17.9142 10.7143 17.858 10.7841 17.7872C10.8538 17.7164 10.9089 17.6325 10.9459 17.5403C10.983 17.4481 11.0013 17.3494 10.9999 17.25V9.75C11.0014 9.64962 10.9827 9.54997 10.9449 9.45695C10.9071 9.36394 10.851 9.27946 10.78 9.20852C10.709 9.13758 10.6244 9.08161 10.5313 9.04395C10.4383 9.00629 10.3386 8.98769 10.2382 8.98926ZM13.7382 8.98926C13.5395 8.99236 13.3501 9.07423 13.2117 9.21686C13.0732 9.3595 12.9971 9.55125 12.9999 9.75V17.25C12.9985 17.3494 13.0169 17.4481 13.054 17.5403C13.091 17.6325 13.146 17.7164 13.2158 17.7872C13.2856 17.858 13.3688 17.9142 13.4605 17.9526C13.5521 17.9909 13.6505 18.0107 13.7499 18.0107C13.8493 18.0107 13.9477 17.9909 14.0394 17.9526C14.1311 17.9142 14.2143 17.858 14.2841 17.7872C14.3538 17.7164 14.4089 17.6325 14.4459 17.5403C14.483 17.4481 14.5013 17.3494 14.4999 17.25V9.75C14.5014 9.64962 14.4827 9.54997 14.4449 9.45695C14.4071 9.36394 14.351 9.27946 14.28 9.20852C14.209 9.13758 14.1244 9.08161 14.0313 9.04395C13.9383 9.00629 13.8386 8.98769 13.7382 8.98926Z"
                  fill={themeColors.neutral890}
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="introductons-wrap">
        {isLoadNotificationList && !isListItems && (
          <div className="align-items-center ml-10">
            <div className="introduction-content pb-0">
              <p className="no-notif-para text-center pt-24 pb-24">
                No new notifications
              </p>
            </div>
          </div>
        )}
        {!isLoadNotificationList && !isListItems ? (
          <IntroductionSkeleton />
        ) : (
          <>
            {isListItems &&
              list.map((notification, index) => {
                return (
                  <>
                    {(notification.SubjectType == 1 ||
                      notification.SubjectType == 2) && (
                      <RenderIntroductionNotification
                        notification={notification}
                      />
                    )}

                    {notification.SubjectType == 3 && (
                      <RenderIntroductionReceiveExpiredNotification
                        notification={notification}
                      />
                    )}

                    {notification.SubjectType == 4 && (
                      <RenderIntroductionSentExpiredNotification
                        notification={notification}
                      />
                    )}
                    {notification.SubjectType == 5 && (
                      <RenderWantAdViewNotification
                        notification={notification}
                      />
                    )}
                    {notification.SubjectType == 24 && (
                      <RenderDMNotification notification={notification} />
                    )}

                    {notification.SubjectType == 6 && (
                      <RenderWantAdCountBasedOnLocationNotification
                        notification={notification}
                      />
                    )}
                    {notification.SubjectType == 7 && (
                      <RenderMatchNotification notification={notification} />
                    )}
                    {notification.SubjectType == 8 && (
                      <RenderIntroductionAcceptedNotification
                        notification={notification}
                      />
                    )}

                    {notification.SubjectType == 9 &&
                      notification.ExpiryType === 1 && (
                        <RenderPlanExpireNotification
                          notification={notification}
                        />
                      )}
                    {notification.SubjectType == 9 &&
                      notification.ExpiryType === 2 && (
                        <RenderCardDeclinedNotification
                          notification={notification}
                        />
                      )}
                    {notification.SubjectType == 9 &&
                      notification.ExpiryType === 3 && (
                        <RenderPlanExpiredNotification
                          notification={notification}
                        />
                      )}
                    {notification.SubjectType == 10 && (
                      <RenderWantadExpireNotification
                        notification={notification}
                      />
                    )}
                    {notification.SubjectType == 22 && (
                      <RenderBuyerListWantAdNotification
                        notification={notification}
                      />
                    )}
                    {notification.SubjectType == 26 && (
                      <RenderWantAdSyndicatedNotification
                        notification={notification}
                      />
                    )}
                    {notification.SubjectType == 12 && (
                      <RenderImageInvalidNotification
                        notification={notification}
                      />
                    )}
                    {notification.SubjectType == 25 && (
                      <RenderAdminNotification notification={notification} />
                    )}
                    {notification.SubjectType == 27 && (
                      <RenderUpgradeNudgeNotification
                        notification={notification}
                      />
                    )}
                  </>
                );
              })}
          </>
        )}
      </div>
    </>
  );
};
export default Notifications;
